<template>
  <div class="jobs-page">
    <section class="jobs-hero">
      <div class="label">
        <div>
          <span class="eyebrow">Catalog automation</span>
          <h4>Scheduled Jobs</h4>
        </div>
        <label v-if="activeWorkspace === 'jobs'" class="schedule-switch adv-toggle">
          <input type="checkbox" :checked="includeHidden" @change="toggleHidden($event)" />
          <span class="switch-track"></span>
          <span>Advanced / internal jobs</span>
        </label>
      </div>
      <p v-if="activeWorkspace === 'jobs'" class="intro">
        Scraping and AI workflows for keeping the catalog useful. <strong>Onboard New Parts</strong>
        runs the full research pipeline &mdash; discover, scrape, and fill missing specs &mdash; in a
        single run. Set how many parts to bring in with <em>quantity</em>, or turn on a simple interval
        so GixCore can keep working in the background.
      </p>
      <p v-else class="intro">
        Review recent automation runs, spot failures, and open detailed diagnostics without expanding
        every workflow on the page.
      </p>
    </section>

    <div v-if="error" class="alert">{{ error }}</div>
    <div v-if="notice" class="notice">{{ notice }}</div>

    <nav class="jobs-view-tabs" aria-label="Scheduled jobs views">
      <button type="button" :class="{ active: activeWorkspace === 'jobs' }" @click="activeWorkspace = 'jobs'">
        <span>Jobs</span>
        <b>{{ displayJobs.length }}</b>
      </button>
      <button type="button" :class="{ active: activeWorkspace === 'history' }" @click="activeWorkspace = 'history'">
        <span>Run history</span>
        <b>{{ runs.length }}</b>
      </button>
    </nav>

    <section class="job-category-tabs" v-if="false && jobCategories.length">
      <button
        v-for="group in jobCategories"
        :key="group.key"
        type="button"
        class="job-category-tab"
        :class="{ active: activeCategory.key === group.key }"
        @click="selectJobCategory(group.key)">
        <span class="category-eyebrow">{{ group.eyebrow }}</span>
        <strong>{{ group.label }}</strong>
        <span class="category-count">
          {{ group.count }} job{{ group.count === 1 ? '' : 's' }}
          <b v-if="group.activeCount">{{ group.activeCount }} running</b>
        </span>
      </button>
    </section>

    <section v-if="false" class="jobs-category-panel">
      <div class="jobs-category-head">
        <div>
          <span class="eyebrow">{{ activeCategory.eyebrow || 'Automation' }}</span>
          <h5>{{ activeCategory.label || 'Jobs' }}</h5>
          <p>{{ activeCategory.description }}</p>
        </div>
        <div class="jobs-head-actions">
          <span class="live-connection" :class="{ online: liveConnected }">
            <i></i>{{ liveConnected ? 'Live' : 'Reconnecting...' }}
          </span>
          <button class="btn-refresh" @click="refreshLive()">Refresh</button>
        </div>
      </div>

      <div class="job-card-grid" v-if="activeCategoryJobs.length">
        <article
          v-for="row in activeCategoryJobs"
          :key="row.key"
          class="job-workflow-card"
          :class="jobCardClass(row)"
          @click="openDetail(row)"
        >
          <div class="job-card-top">
            <div class="job-meta-row">
              <span class="cat-badge" :class="'cat-' + jobCategoryKey(row)">{{ jobCategoryMeta(row).label }}</span>
              <span v-if="row.key === primaryJobKey" class="featured-tag">Main workflow</span>
              <span v-if="row.hidden" class="hidden-tag">Internal</span>
              <span v-if="isTestRun(row.lastRun)" class="test-run-tag">Last run: test</span>
            </div>
            <span class="status-pill" :class="['st-' + jobDisplayStatus(row), { 'is-live': isActive(row) }]">
              {{ statusLabel(jobDisplayStatus(row)) }}
            </span>
          </div>

          <div class="job-card-title-row">
            <div>
              <h5>{{ row.name }}</h5>
              <p>{{ row.description }}</p>
            </div>
          </div>

          <div v-if="aiPromptBadges(row).length" class="ai-badge-row job-ai-badge-row">
            <button
              v-for="prompt in aiPromptBadges(row)"
              :key="prompt.key"
              type="button"
              class="ai-prompt-badge job-prompt-badge"
              :class="{ warning: prompt.requiresSeed }"
              :title="aiPromptTitle(prompt)"
              @click.stop="openPrompt(prompt.key)"
            >
              <span>{{ prompt.name || prompt.key }}</span>
              <small>{{ aiPromptMeta(prompt) }}</small>
              <b v-if="prompt.requiresSeed">Prompt not seeded</b>
            </button>
          </div>

          <div class="job-card-facts">
            <div>
              <span>Last run</span>
              <strong v-if="row.lastRun" :title="fmtTime(row.lastRun.startedAt)">{{ fmtRelative(row.lastRun.startedAt) }}</strong>
              <strong v-else>Not run yet</strong>
            </div>
            <div>
              <span>Schedule</span>
              <strong :class="{ 'sched-auto': row.enabled }">
                <i v-if="row.enabled" class="sched-dot"></i>{{ row.enabled ? cronHint(row.cron) : 'Manual only' }}
              </strong>
            </div>
          </div>

          <div v-if="row.enabled" class="job-card-auto">
            <span class="auto-tag">Auto</span>
            <span class="auto-config">
              <span v-for="chip in configSummaryChips(row).slice(0, 3)" :key="chip.key"
                    class="auto-chip" :class="{ 'is-changed': !chip.isDefault }">
                {{ chip.label }}: {{ chip.value }}
              </span>
              <span v-if="!configSummaryChips(row).length" class="auto-chip-empty">default settings</span>
            </span>
            <span v-if="nextRunHint(row)" class="auto-next">{{ nextRunHint(row) }}</span>
          </div>

          <div class="job-card-result">
            <div class="job-stats" v-if="lastRunModel(row).summary.length">
              <span v-for="chip in lastRunModel(row).summary" :key="chip.key"
                    class="stat-chip" :class="['tone-' + chip.tone, { 'is-zero': chip.isZero }]">
                <span v-if="lastRunModel(row).multiStage && chip.stage" class="chip-stage">{{ chip.stage }}</span>
                <span class="chip-key">{{ chip.label }}</span>
                <span class="chip-val">{{ chip.value }}<span v-if="chip.denom" class="chip-denom">/{{ chip.denom }}</span></span>
              </span>
            </div>
            <span v-else class="job-stats-empty">No runs yet</span>
          </div>

          <div class="job-card-actions">
            <div class="job-card-action-buttons">
              <button class="btn-run" :class="runButtonClass(row)" :disabled="isActive(row) || !jobCanRun(row)" @click.stop="runJob(row)">
                {{ runButtonLabel(row) }}
              </button>
              <button
                v-if="testDeliveryFor(row)"
                class="btn-test-delivery"
                :disabled="isActive(row) || testDeliveryStarting[row.key]"
                @click.stop="sendTestDelivery(row)"
              >
                {{ testDeliveryButtonLabel(row) }}
              </button>
            </div>
            <a @click.stop="openDetail(row)">settings</a>
          </div>
        </article>
      </div>
      <div v-else class="jobs-empty">No jobs in this category.</div>
    </section>

    <section v-if="activeWorkspace === 'jobs'" class="jobs-table-toolbar">
      <div class="jobs-category-filter" role="group" aria-label="Job category">
        <button
          v-for="group in jobCategories"
          :key="group.key"
          type="button"
          :class="{ active: activeCategory.key === group.key }"
          @click="selectJobCategory(group.key)"
        >
          <span>{{ group.label }}</span>
          <b>{{ group.count }}</b>
          <i v-if="group.activeCount">{{ group.activeCount }} running</i>
        </button>
      </div>
      <div class="jobs-head-actions">
        <span class="live-connection" :class="{ online: liveConnected }">
          <i></i>{{ liveConnected ? 'Live' : 'Reconnecting...' }}
        </span>
        <button class="btn-refresh" @click="refreshLive()">Refresh</button>
      </div>
      <p v-if="activeCategory.description" class="active-category-description">{{ activeCategory.description }}</p>
    </section>

    <AdminTable
      v-if="activeWorkspace === 'jobs'"
      class="jobs-admin-table"
      :columns="jobColumns"
      :rows="activeCategoryJobs"
      :row-key="'key'"
      :row-class="jobRowClass"
      :loading="false"
      empty-text="No jobs found."
      clickable
      @row-click="openDetail"
    >
      <template v-slot:empty>
        <span>No jobs in this category.</span>
      </template>
      <template v-slot:cell-job="{ row }">
        <div class="job-cell-main">
          <div v-if="row.key === primaryJobKey || row.hidden" class="job-meta-row">
            <span v-if="row.key === primaryJobKey" class="featured-tag">Main workflow</span>
            <span v-if="row.hidden" class="hidden-tag">Internal</span>
          </div>
          <div class="job-name-line">{{ row.name }}</div>
          <div class="job-desc-sub">{{ row.description }}</div>
          <span v-if="includeHidden && aiPromptBadges(row).length" class="job-advanced-note">
            {{ aiPromptBadges(row).length }} AI prompt{{ aiPromptBadges(row).length === 1 ? '' : 's' }} configured
          </span>
        </div>
      </template>
      <template v-slot:cell-status="{ row }">
        <span class="status-pill" :class="['st-' + jobDisplayStatus(row), { 'is-live': isActive(row) }]">
          {{ executionStatusLabel(jobDisplayStatus(row)) }}
        </span>
      </template>
      <template v-slot:cell-lastrun="{ row }">
        <span v-if="row.lastRun" :title="fmtTime(row.lastRun.startedAt)">{{ fmtTableTime(row.lastRun.startedAt) }}</span>
        <span v-else>Not run yet</span>
        <span v-if="row.lastRun" class="sched-sub">{{ duration(row.lastRun) }}</span>
      </template>
      <template v-slot:cell-schedule="{ row }">
        <span class="job-schedule-state" :class="{ automated: row.enabled }">
          <i></i>{{ row.enabled ? cronHint(row.cron) : 'Manual only' }}
        </span>
        <span v-if="row.enabled && nextRunHint(row)" class="sched-sub">{{ nextRunHint(row) }}</span>
      </template>
      <template v-slot:cell-result="{ row }">
        <span v-if="row.lastRun" class="job-result-summary" :class="'tone-' + lastRunModel(row).currentTone">
          {{ jobResultLabel(row) }}
        </span>
        <span v-else class="job-stats-empty">No result yet</span>
      </template>
      <template v-slot:cell-actions="{ row }">
        <div class="job-row-actions">
          <button class="btn-run" :class="runButtonClass(row)" :disabled="isActive(row) || !jobCanRun(row)" @click.stop="runJob(row)">
            {{ runButtonLabel(row) }}
          </button>
          <button class="btn-row-settings" type="button" @click.stop="openDetail(row)">Settings</button>
        </div>
      </template>
    </AdminTable>

    <!-- Job detail drawer -->
    <div v-if="detailJob" class="job-drawer" @click.self="closeDetail()">
      <div class="job-panel">
        <div class="panel-head">
          <div class="panel-head-main">
            <div class="job-meta-row">
              <span class="cat-badge" :class="'cat-' + jobCategoryKey(detailJob)">{{ jobCategoryMeta(detailJob).label }}</span>
              <span v-if="detailJob.key === primaryJobKey" class="featured-tag">Main workflow</span>
              <span v-if="detailJob.hidden" class="hidden-tag">Internal</span>
              <span class="status-pill" :class="['st-' + jobDisplayStatus(detailJob), { 'is-live': isActive(detailJob) }]">
                {{ executionStatusLabel(jobDisplayStatus(detailJob)) }}
              </span>
            </div>
            <h5 class="panel-title">{{ detailJob.name }}</h5>
          </div>
          <a class="close" @click="closeDetail()">×</a>
        </div>

        <div class="panel-body">
          <p class="panel-desc">{{ detailJob.description }}</p>

          <section class="job-section" v-if="aiPromptBadges(detailJob).length">
            <div class="section-title">AI prompts</div>
            <div class="ai-badge-row panel-ai-row">
              <button
                v-for="prompt in aiPromptBadges(detailJob)"
                :key="prompt.key"
                type="button"
                class="ai-prompt-badge"
                :class="{ warning: prompt.requiresSeed }"
                :title="aiPromptTitle(prompt)"
                @click="openPrompt(prompt.key)"
              >
                <span>{{ prompt.name || prompt.key }}</span>
                <small>{{ aiPromptMeta(prompt) }}</small>
                <b v-if="prompt.requiresSeed">Prompt not seeded</b>
              </button>
            </div>
          </section>

          <section class="job-section" v-if="jobOptions(detailJob).length && optionValues[detailJob.key]">
            <div class="section-title-row">
              <div>
                <div class="section-title">Run settings</div>
                <p class="section-subnote">
                  Apply to <strong>Run Now</strong> immediately, and to automatic runs
                  once you press <strong>Save settings</strong>.
                </p>
              </div>
              <div class="config-save">
                <span class="config-state" :class="configStateClass(detailJob)">{{ configStateLabel(detailJob) }}</span>
                <button
                  class="btn-save-config"
                  :disabled="!configDirty(detailJob) || configSaving[detailJob.key]"
                  @click="saveConfig(detailJob)">
                  {{ configSaving[detailJob.key] ? 'Saving…' : 'Save settings' }}
                </button>
              </div>
            </div>
            <div class="job-options" @input="queueScopePreview(detailJob)" @change="queueScopePreview(detailJob)">
              <div class="opt-row" :class="{ 'opt-row-wide': isNoteOpt(opt) }"
                   v-for="opt in jobOptions(detailJob)" :key="opt.key">
                <label>{{ isNoteOpt(opt) ? (opt.label || 'AI research note') : prettyLabel(opt.label || opt.key) }}</label>
                <select v-if="opt.type === 'select'" v-model="optionValues[detailJob.key][opt.key]">
                  <option v-for="c in opt.choices" :key="c" :value="c">{{ c }}</option>
                </select>
                <input v-else-if="opt.type === 'number'" type="number"
                       v-model.number="optionValues[detailJob.key][opt.key]" />
                <label v-else-if="opt.type === 'boolean'" class="opt-switch">
                  <span class="opt-switch-text" :class="{ on: optionValues[detailJob.key][opt.key] }">
                    {{ optionValues[detailJob.key][opt.key] ? 'On' : 'Off' }}
                  </span>
                  <input type="checkbox" v-model="optionValues[detailJob.key][opt.key]" />
                  <span class="switch-track"></span>
                </label>
                <textarea v-else-if="isNoteOpt(opt)" class="opt-textarea" rows="2"
                          :placeholder="researchPlaceholder(detailJob)"
                          v-model="optionValues[detailJob.key][opt.key]"></textarea>
                <input v-else type="text" v-model="optionValues[detailJob.key][opt.key]" />
              </div>
            </div>
            <div v-if="dedupeMutationWarning(detailJob)" class="job-mutation-warning" :class="{ danger: dedupeMode(detailJob) === 'delete' }">
              <strong>{{ dedupeMode(detailJob) === 'delete' ? 'Destructive mode selected' : 'Mutating mode selected' }}</strong>
              <span>{{ dedupeMutationWarning(detailJob) }}</span>
            </div>
          </section>

          <section class="job-section scope-section">
            <div class="section-title">Scope preview</div>
            <div v-if="scopeLoading" class="scope-state">Checking eligible work...</div>
            <div v-else-if="scopeError" class="scope-state scope-error">{{ scopeError }}</div>
            <div v-else-if="scopePreview" class="scope-preview">
              <div class="scope-head">
                <div>
                  <strong>{{ scopeDescriptorTitle(scopePreview) }}</strong>
                  <p>{{ scopeDescriptorDescription(scopePreview) }}</p>
                </div>
                <div class="scope-pills">
                  <span class="scope-pill quiet" :class="{ blocked: scopePreview.canPreview === false }">
                    {{ scopePreview.canPreview === false ? 'No preview' : 'Preview available' }}
                  </span>
                  <span class="scope-pill" :class="{ blocked: scopeReadinessBlocked(scopePreview, detailJob) }">
                    {{ scopeReadinessLabel(scopePreview, detailJob) }}
                  </span>
                </div>
              </div>
              <div v-if="scopeRunWarning(scopePreview, detailJob)" class="scope-warning">
                {{ scopeRunWarning(scopePreview, detailJob) }}
              </div>

              <div v-if="scopeCountGroups(scopePreview, detailJob).length" class="scope-count-groups">
                <div v-for="group in scopeCountGroups(scopePreview, detailJob)" :key="group.key" class="scope-count-group">
                  <span>{{ group.label }}</span>
                  <div>
                    <b v-for="item in group.items" :key="item.key">
                      {{ item.label }} <em>{{ item.value }}</em>
                    </b>
                  </div>
                </div>
              </div>

              <div v-if="scopeWorkflowStages(scopePreview).length" class="scope-workflow">
                <div
                  v-for="(stage, index) in scopeWorkflowStages(scopePreview)"
                  :key="stage.key || stage.name || index"
                  class="scope-stage">
                  <span>Stage {{ index + 1 }}</span>
                  <strong>{{ stage.title || stage.label || stage.name || stage.key }}</strong>
                  <p>{{ stage.description || stage.summary || stage.status || '' }}</p>
                </div>
              </div>

              <div v-if="scopeDetailGroups(scopePreview).length" class="scope-detail-groups">
                <div v-for="group in scopeDetailGroups(scopePreview)" :key="group.key" class="scope-detail-group">
                  <span>{{ group.label }}</span>
                  <div>
                    <b v-for="item in group.items" :key="item.key">
                      {{ item.label }} <em>{{ item.value }}</em>
                    </b>
                  </div>
                </div>
              </div>

              <div v-if="onboardDirectiveItems(scopePreview).length" class="scope-directives">
                <span class="scope-items-title">Actionable onboarding plan</span>
                <div
                  v-for="(item, idx) in onboardDirectiveItems(scopePreview)"
                  :key="scopeItemKey(item) + ':' + idx"
                  class="scope-directive"
                  :class="['tier-' + scopeDirectiveTier(item), { 'is-selected': item.selected }]"
                >
                  <span class="sd-rank">{{ idx + 1 }}</span>
                  <span class="sd-tier tier-badge" :class="'tier-' + scopeDirectiveTier(item)">{{ scopeDirectiveTierLabel(item) }}</span>
                  <div class="sd-main">
                    <div class="sd-title-row">
                      <span class="sd-title">{{ scopeDirectiveTitle(item) }}</span>
                      <span v-if="item.blockedCpus" class="sd-blocks">blocks {{ item.blockedCpus }} CPU{{ item.blockedCpus === 1 ? '' : 's' }}</span>
                    </div>
                    <div v-if="scopeDirectiveConstraints(item).length" class="sd-cons">
                      <span v-for="(con, ci) in scopeDirectiveConstraints(item)" :key="ci" class="sd-con">{{ con }}</span>
                    </div>
                    <div v-if="item.note" class="sd-note">{{ item.note }}</div>
                  </div>
                  <span class="sd-flag" :class="item.selected ? 'in-run' : 'not-selected'">
                    {{ item.selected ? 'In this run' : 'Not selected' }}
                  </span>
                </div>
              </div>

              <div v-if="manualReviewDirectiveItems(scopePreview).length" class="scope-directives scope-directives-manual">
                <span class="scope-items-title">Manual review &mdash; not executed</span>
                <div
                  v-for="(item, idx) in manualReviewDirectiveItems(scopePreview)"
                  :key="scopeItemKey(item) + ':manual:' + idx"
                  class="scope-directive retire is-report-only"
                  :class="'tier-' + scopeDirectiveTier(item)"
                >
                  <span class="sd-rank">{{ idx + 1 }}</span>
                  <span class="sd-tier tier-badge" :class="'tier-' + scopeDirectiveTier(item)">{{ scopeDirectiveTierLabel(item) }}</span>
                  <div class="sd-main">
                    <div class="sd-title-row">
                      <span class="sd-title">{{ scopeDirectiveTitle(item) }}</span>
                      <span v-if="item.blockedCpus" class="sd-blocks">blocks {{ item.blockedCpus }} CPU{{ item.blockedCpus === 1 ? '' : 's' }}</span>
                    </div>
                    <div v-if="scopeDirectiveConstraints(item).length" class="sd-cons">
                      <span v-for="(con, ci) in scopeDirectiveConstraints(item)" :key="ci" class="sd-con">{{ con }}</span>
                    </div>
                    <div v-if="item.note" class="sd-note">{{ item.note }}</div>
                  </div>
                  <span class="sd-flag report-only">Report only</span>
                </div>
              </div>

              <div v-if="!hasOnboardingDirectiveItems(scopePreview) && scopeItems(scopePreview).length" class="scope-items">
                <span class="scope-items-title">This run will work on these eligible rows first</span>
                <table>
                  <thead>
                    <tr>
                      <th>Entity</th>
                      <th>Type</th>
                      <th>ID</th>
                      <th>Model</th>
                      <th>Status</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in scopeItems(scopePreview)" :key="scopeItemKey(item)">
                      <td>{{ item.entity || '-' }}</td>
                      <td>{{ item.type || item.part_type || '-' }}</td>
                      <td>{{ item.id || item.target_id || '-' }}</td>
                      <td>{{ item.model || item.name || item.asin || '-' }}</td>
                      <td>{{ item.status || '-' }}</td>
                      <td>{{ item.reason || '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else class="scope-state">No scope preview available for this job yet.</div>
          </section>

          <section class="job-section">
            <div class="section-title">Schedule</div>
            <div class="schedule-top">
              <label class="schedule-switch">
                <input type="checkbox" :checked="detailJob.enabled" @change="toggleEnabled(detailJob, $event)" />
                <span class="switch-track"></span>
                <span>{{ detailJob.enabled ? 'Automatic' : 'Manual only' }}</span>
              </label>
            </div>
            <div v-if="detailJob.enabled && scheduleDrafts[detailJob.key]" class="schedule-builder">
              <span>Run every</span>
              <input
                class="schedule-number"
                type="number"
                min="1"
                :max="scheduleMax(scheduleDrafts[detailJob.key].unit)"
                v-model.number="scheduleDrafts[detailJob.key].every"
                @change="saveScheduleFromDraft(detailJob)"
              />
              <select
                class="schedule-unit"
                v-model="scheduleDrafts[detailJob.key].unit"
                @change="saveScheduleFromDraft(detailJob)"
              >
                <option value="hours">hours</option>
                <option value="days">days</option>
                <option value="weeks">week</option>
              </select>
            </div>
            <div class="schedule-meta">
              <span>{{ scheduleHint(detailJob) }}</span>
              <span v-if="detailJob.enabled && detailJob.cron" class="cron-code">{{ detailJob.cron }}</span>
            </div>

            <div v-if="detailJob.enabled" class="schedule-summary" :class="{ 'has-warning': configDirty(detailJob) }">
              <div class="ss-head">
                <span class="ss-badge">Automatic</span>
                <strong>Runs {{ scheduleSummaryLine(detailJob) }}</strong>
              </div>
              <div v-if="nextRunHint(detailJob)" class="ss-next">
                <span class="ss-next-label">Next run</span>
                <span class="ss-next-value">{{ nextRunHint(detailJob) }}</span>
              </div>
              <div class="ss-config">
                <span class="ss-config-label">Will run with</span>
                <div class="ss-chips">
                  <span v-for="chip in configSummaryChips(detailJob)" :key="chip.key"
                        class="ss-chip" :class="{ 'is-changed': !chip.isDefault }">
                    <b>{{ chip.label }}</b>
                    <span>{{ chip.value }}</span>
                  </span>
                  <span v-if="!configSummaryChips(detailJob).length" class="ss-chip-empty">default settings</span>
                </div>
              </div>
              <div v-if="configDirty(detailJob)" class="ss-warn">
                Unsaved changes above won't apply to automatic runs until you press
                <strong>Save settings</strong>.
              </div>
            </div>
          </section>

          <section class="job-section">
            <div class="section-title">Last run</div>
            <div class="panel-lastrun">
              <span v-if="detailJob.lastRun">{{ fmtTime(detailJob.lastRun.startedAt) }}</span>
              <span v-else>Not run yet</span>
            </div>
            <div class="job-stats" v-if="lastRunModel(detailJob).summary.length">
              <span v-for="chip in lastRunModel(detailJob).summary" :key="chip.key"
                    class="stat-chip" :class="['tone-' + chip.tone, { 'is-zero': chip.isZero }]">
                <span v-if="lastRunModel(detailJob).multiStage && chip.stage" class="chip-stage">{{ chip.stage }}</span>
                <span class="chip-key">{{ chip.label }}</span>
                <span class="chip-val">{{ chip.value }}<span v-if="chip.denom" class="chip-denom">/{{ chip.denom }}</span></span>
              </span>
            </div>
          </section>
        </div>

        <div class="panel-foot">
          <div class="panel-foot-actions" :class="{ 'has-test-delivery': testDeliveryFor(detailJob) }">
            <button class="btn-run wide" :class="runButtonClass(detailJob)" :disabled="isActive(detailJob) || !jobCanRun(detailJob)" @click="runJob(detailJob)">
              {{ runButtonLabel(detailJob) }}
            </button>
            <button
              v-if="testDeliveryFor(detailJob)"
              class="btn-test-delivery wide"
              :disabled="isActive(detailJob) || testDeliveryStarting[detailJob.key]"
              @click="sendTestDelivery(detailJob)"
            >
              {{ testDeliveryButtonLabel(detailJob) }}
            </button>
          </div>
          <p class="foot-note">
            Runs once now with the settings above.
            <span v-if="testDeliveryFor(detailJob)" class="test-delivery-note">
              The test is prefixed [TEST] and sent only to your signed-in admin email.
            </span>
            <span v-if="detailJob.enabled && configDirty(detailJob)" class="foot-note-warn">
              Automatic runs still use the last saved config — Save settings to change them.
            </span>
          </p>
        </div>
      </div>
    </div>

    <div v-if="activeWorkspace === 'history'" class="label runs-label">
      <div>
        <span class="eyebrow">Audit trail</span>
        <h4>Run History</h4>
        <small class="runs-category-note">Completed runs stay compact. Open details only when you need diagnostics.</small>
      </div>
      <div class="runs-controls">
        <select class="runs-filter" v-model="runJobFilter">
          <option value="all">All jobs</option>
          <option v-for="opt in jobFilterOptions" :key="opt.key" :value="opt.key">{{ opt.name }}</option>
        </select>
        <select class="runs-filter" v-model="runStatusFilter">
          <option value="all">Any status</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="running">Running</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button class="btn-refresh" @click="fetchAll()">Refresh</button>
      </div>
    </div>

    <div v-if="false" class="runs-history-list">
      <div class="runs-history-head">
        <span>Job</span>
        <span>Status</span>
        <span>Trigger</span>
        <span>Started</span>
        <span>Duration</span>
        <span></span>
      </div>

      <article
        v-for="row in filteredRuns"
        :key="row.id"
        class="run-history-card"
        :class="runRowClass(row)"
      >
        <div class="run-history-main">
          <div class="run-job-title">
            <span class="job-cell">{{ jobName(row.jobKey) }}</span>
            <span class="run-id">Run #{{ row.id }} <b v-if="isTestRun(row)" class="test-run-tag">Test email</b></span>
          </div>
          <div>
            <span class="status-pill" :class="['st-' + row.status, { 'is-live': isRunning(row) }]">
              {{ statusLabel(row.status) }}
            </span>
          </div>
          <span class="trigger-cell">{{ row.trigger }}</span>
          <span :title="fmtTime(row.startedAt)">{{ fmtRelative(row.startedAt) }}</span>
          <span>{{ duration(row) }}</span>
          <div class="row-actions">
            <a @click="viewRun(row)">log</a>
            <a v-if="row.status === 'running' || row.status === 'queued'"
               class="cancel-link" @click="cancelRun(row)">cancel</a>
          </div>
        </div>

        <div class="run-history-result">
          <div class="result-cell">
            <div v-if="row.errorText" class="result-error">{{ row.errorText }}</div>
            <template v-else>
              <div class="run-pipeline-head">
                <div class="run-pipeline-copy">
                  <span class="run-stage-current" :class="'stage-' + row.result.currentTone">
                    {{ row.result.headline || 'No output' }}
                  </span>
                  <span v-if="row.result.stageLine" class="run-stage-line">{{ row.result.stageLine }}</span>
                  <span v-if="runAiLabel(row)" class="run-ai-line">{{ runAiLabel(row) }}</span>
                </div>
                <button v-if="row.result.detail" class="result-toggle" @click="toggleExpand(row)">
                  {{ isExpanded(row) ? 'Hide' : 'Details' }}
                  <span class="chev" :class="{ open: isExpanded(row) }">&rsaquo;</span>
                </button>
              </div>

              <div v-if="row.result.stages.length" class="run-stage-track">
                <div
                  v-for="stage in row.result.stages"
                  :key="'stage-' + stage.key"
                  class="run-stage-step"
                  :class="'is-' + stage.status"
                >
                  <span class="stage-dot"></span>
                  <span class="stage-copy">
                    <span class="stage-name">{{ stage.label }}</span>
                    <span class="stage-summary">{{ stage.summary }}</span>
                  </span>
                  <button
                    v-if="stage.promptKey"
                    class="stage-prompt"
                    @click.stop="openPrompt(stage.promptKey)"
                  >{{ stage.promptKey }}</button>
                </div>
              </div>

              <div v-else class="result-summary">
                <span v-for="chip in row.result.summary" :key="'s-' + chip.key"
                      class="stat-chip" :class="['tone-' + chip.tone, { 'is-zero': chip.isZero }]">
                  <span class="chip-key">{{ chip.label }}</span>
                  <span class="chip-val">{{ chip.value }}<span v-if="chip.denom" class="chip-denom">/{{ chip.denom }}</span></span>
                </span>
                <span v-if="!row.result.summary.length" class="result-empty">No output</span>
              </div>

              <div v-if="isExpanded(row)" class="result-detail">
                <OnboardingRunSummary v-if="isOnboardingRun(row)" :run="row" />
                <template v-else>
                <div v-if="row.result.note" class="result-note">
                  <span class="note-label">AI research note</span>
                  <span class="note-text">{{ row.result.note }}</span>
                </div>
                <div v-if="aiPromptBadges(row).length || aiPromptUsage(row).length" class="stage-detail-card ai-detail-card">
                  <div class="stage-detail-head">
                    <span class="stage-detail-title">AI prompts</span>
                    <span v-if="runAiLabel(row)" class="run-ai-line">{{ runAiLabel(row) }}</span>
                  </div>
                  <div v-if="aiPromptBadges(row).length" class="ai-badge-row">
                    <button
                      v-for="prompt in aiPromptBadges(row)"
                      :key="prompt.key"
                      type="button"
                      class="ai-prompt-badge"
                      :class="{ warning: prompt.requiresSeed }"
                      :title="aiPromptTitle(prompt)"
                      @click.stop="openPrompt(prompt.key)"
                    >
                      <span>{{ prompt.name || prompt.key }}</span>
                      <small>{{ aiPromptMeta(prompt) }}</small>
                      <b v-if="prompt.requiresSeed">Prompt not seeded</b>
                    </button>
                  </div>
                  <div v-if="aiPromptUsage(row).length" class="ai-usage-grid">
                    <span v-for="usage in aiPromptUsage(row)" :key="usage.promptKey || usage.key" class="ai-usage-chip">
                      <b>{{ usage.promptKey || usage.key }}</b>
                      <small>{{ usage.calls || 0 }} calls / {{ usage.success || 0 }} ok / {{ usage.failed || 0 }} failed</small>
                    </span>
                  </div>
                </div>
                <div v-if="aiOutcome(row).has" class="stage-detail-card ai-outcome-card">
                  <div class="stage-detail-head">
                    <span class="stage-detail-title">AI outcome</span>
                    <span class="ai-outcome-chip" :class="'tone-' + aiOutcome(row).tone">{{ aiOutcome(row).resultLabel }}</span>
                  </div>
                  <div class="ai-outcome-meta">
                    <span v-if="aiOutcome(row).items !== null">Items {{ aiOutcome(row).items }}</span>
                    <span v-if="aiOutcome(row).webSearchCalls !== null">Web search calls {{ aiOutcome(row).webSearchCalls }}</span>
                    <span v-if="aiOutcome(row).status">Status {{ aiOutcome(row).status }}</span>
                  </div>
                  <details v-if="aiOutcome(row).preview || aiOutcome(row).blocks.length" class="ai-output-preview">
                    <summary>Model output preview</summary>
                    <pre v-if="aiOutcome(row).preview">{{ aiOutcome(row).preview }}</pre>
                    <pre v-for="(block, blockIndex) in aiOutcome(row).blocks" :key="'ai-block-' + blockIndex">{{ block }}</pre>
                  </details>
                </div>
                <div v-for="stage in row.result.stages" :key="'detail-' + stage.key" class="stage-detail-card">
                  <div class="stage-detail-head">
                    <span class="stage-detail-title">{{ stage.label }}</span>
                    <span class="stage-status-pill" :class="'is-' + stage.status">{{ stage.statusLabel }}</span>
                    <button
                      v-if="stage.promptKey"
                      class="stage-prompt detail"
                      @click.stop="openPrompt(stage.promptKey)"
                    >Prompt: {{ stage.promptKey }}</button>
                  </div>
                  <div class="stage-detail-summary">{{ stage.summary }}</div>
                  <div class="stat-chips">
                    <span v-for="chip in stage.chips" :key="chip.key"
                          class="stat-chip" :class="['tone-' + chip.tone, { 'is-zero': chip.isZero }]">
                      <span class="chip-key">{{ chip.label }}</span>
                      <span class="chip-val">{{ chip.value }}<span v-if="chip.denom" class="chip-denom">/{{ chip.denom }}</span></span>
                    </span>
                  </div>
                </div>
                <div v-for="group in row.result.looseGroups" :key="'g-' + group.stage" class="stat-group">
                  <span v-if="group.stage" class="stage-label">{{ group.stage }}</span>
                  <div class="stat-chips">
                    <span v-for="chip in group.chips" :key="chip.key"
                          class="stat-chip" :class="['tone-' + chip.tone, { 'is-zero': chip.isZero }]">
                      <span class="chip-key">{{ chip.label }}</span>
                      <span class="chip-val">{{ chip.value }}<span v-if="chip.denom" class="chip-denom">/{{ chip.denom }}</span></span>
                    </span>
                  </div>
                </div>
                <div v-if="row.result.params.length" class="stat-group params">
                  <span class="stage-label">Settings</span>
                  <div class="stat-chips">
                    <span v-for="chip in row.result.params" :key="chip.key" class="stat-chip is-param">
                      <span class="chip-key">{{ chip.label }}</span>
                      <span class="chip-val">{{ chip.value }}</span>
                    </span>
                  </div>
                </div>
                <div v-if="dedupeReportRows(row).length" class="dedupe-report">
                  <div class="dedupe-report-head">
                    <span class="stage-label">Duplicate product report</span>
                    <small>{{ dedupeReportRows(row).length }} group{{ dedupeReportRows(row).length === 1 ? '' : 's' }}</small>
                  </div>
                  <table>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>ASIN</th>
                        <th>Kept id</th>
                        <th>Dropped ids</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in dedupeReportRows(row)" :key="item.key">
                        <td>{{ item.type }}</td>
                        <td>{{ item.asin }}</td>
                        <td>{{ item.keep }}</td>
                        <td>{{ item.drop }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                </template>
              </div>
            </template>
          </div>
        </div>
      </article>

      <div v-if="!filteredRuns.length" class="runs-history-empty">
        {{ runs.length ? 'No runs match these filters.' : 'No runs yet.' }}
      </div>
    </div>

    <div v-if="activeWorkspace === 'history'" class="runs-table-wrap compact-runs-table">
      <table class="runs-table">
        <thead>
          <tr>
            <th>Job</th><th>Run status</th><th>Trigger</th><th>Started</th>
            <th>Duration</th><th>Result</th><th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="run in filteredRuns" :key="run.id"
              :class="['row-' + run.status, { 'is-expanded': isExpanded(run) }]">
            <td class="job-cell">
              <strong>{{ jobName(run.jobKey) }}</strong>
              <span class="run-id">Run #{{ run.id }} <b v-if="isTestRun(run)" class="test-run-tag">Test</b></span>
            </td>
            <td>
              <span class="status-pill" :class="['st-' + run.status, { 'is-live': isRunning(run) }]">
                {{ executionStatusLabel(run.status) }}
              </span>
            </td>
            <td class="trigger-cell">{{ run.trigger }}</td>
            <td :title="fmtTime(run.startedAt)">{{ fmtRelative(run.startedAt) }}</td>
            <td>{{ duration(run) }}</td>
            <td class="result-cell">
              <div v-if="run.errorText" class="result-error">{{ run.errorText }}</div>
              <template v-else>
                <div class="result-summary compact-result-summary">
                  <span class="run-result-headline" :class="'tone-' + run.result.currentTone">
                    {{ run.result.headline || run.result.stageLine || 'No output' }}
                  </span>
                </div>
                <div v-if="false && isExpanded(run)" class="result-detail compact-result-detail">
                  <OnboardingRunSummary v-if="isOnboardingRun(run)" :run="run" />
                  <template v-else>
                  <div v-if="run.result.note" class="result-note">
                    <span class="note-label">AI research note</span>
                    <span class="note-text">{{ run.result.note }}</span>
                  </div>
                  <div v-for="group in run.result.groups" :key="'g-' + group.stage" class="stat-group">
                    <span v-if="group.stage" class="stage-label">{{ group.stage }}</span>
                    <div class="stat-chips">
                      <span v-for="chip in group.chips" :key="chip.key"
                            class="stat-chip" :class="['tone-' + chip.tone, { 'is-zero': chip.isZero }]">
                        <span class="chip-key">{{ chip.label }}</span>
                        <span class="chip-val">{{ chip.value }}<span v-if="chip.denom" class="chip-denom">/{{ chip.denom }}</span></span>
                      </span>
                    </div>
                  </div>
                  <div v-if="run.result.params.length" class="stat-group params">
                    <span class="stage-label">Settings</span>
                    <div class="stat-chips">
                      <span v-for="chip in run.result.params" :key="chip.key" class="stat-chip is-param">
                        <span class="chip-key">{{ chip.label }}</span>
                        <span class="chip-val">{{ chip.value }}</span>
                      </span>
                    </div>
                  </div>
                  </template>
                </div>
              </template>
            </td>
            <td class="row-actions">
              <div class="row-actions-inner">
                <a @click="viewRun(run)">Details</a>
                <a v-if="run.status === 'running' || run.status === 'queued'"
                   class="cancel-link" @click="cancelRun(run)">cancel</a>
              </div>
            </td>
          </tr>
          <tr v-if="!filteredRuns.length">
            <td colspan="7" class="empty">{{ runs.length ? 'No runs match these filters.' : 'No runs yet.' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="selectedRun" class="log-drawer" @click.self="closeSelectedRun">
      <div class="log-panel">
        <div class="log-head">
          <span>{{ jobName(selectedRun.jobKey) }} - run #{{ selectedRun.id }} -
            <span class="status-pill" :class="'st-' + selectedRun.status">{{ executionStatusLabel(selectedRun.status) }}</span>
            <span v-if="isTestRun(selectedRun)" class="test-run-tag">Test email</span>
          </span>
          <a class="close" @click="closeSelectedRun">x</a>
        </div>
        <OnboardingRunSummary v-if="isOnboardingRun(selectedRun)" :run="selectedRun" class="log-summary" />
        <template v-else>
        <div v-if="selectedRunNote" class="log-note">
          <span class="note-label">AI research note</span>
          <span class="note-text">{{ selectedRunNote }}</span>
        </div>
        <div v-if="aiPromptBadges(selectedRun).length || aiPromptUsage(selectedRun).length" class="log-ai-section">
          <div class="section-title">AI prompts</div>
          <div v-if="aiPromptBadges(selectedRun).length" class="ai-badge-row">
            <button
              v-for="prompt in aiPromptBadges(selectedRun)"
              :key="prompt.key"
              type="button"
              class="ai-prompt-badge"
              :class="{ warning: prompt.requiresSeed }"
              :title="aiPromptTitle(prompt)"
              @click="openPrompt(prompt.key)"
            >
              <span>{{ prompt.name || prompt.key }}</span>
              <small>{{ aiPromptMeta(prompt) }}</small>
              <b v-if="prompt.requiresSeed">Prompt not seeded</b>
            </button>
          </div>
          <div v-if="aiPromptUsage(selectedRun).length" class="ai-usage-grid">
            <span v-for="usage in aiPromptUsage(selectedRun)" :key="usage.promptKey || usage.key" class="ai-usage-chip">
              <b>{{ usage.promptKey || usage.key }}</b>
              <small>{{ usage.provider || 'default' }} / {{ usage.model || 'default' }} - {{ usage.calls || 0 }} calls, {{ usage.failed || 0 }} failed</small>
            </span>
          </div>
        </div>
        <div v-if="aiOutcome(selectedRun).has" class="log-ai-section">
          <div class="section-title">AI outcome</div>
          <div class="ai-outcome-meta drawer">
            <span class="ai-outcome-chip" :class="'tone-' + aiOutcome(selectedRun).tone">{{ aiOutcome(selectedRun).resultLabel }}</span>
            <span v-if="aiOutcome(selectedRun).items !== null">Items {{ aiOutcome(selectedRun).items }}</span>
            <span v-if="aiOutcome(selectedRun).webSearchCalls !== null">Web search calls {{ aiOutcome(selectedRun).webSearchCalls }}</span>
            <span v-if="aiOutcome(selectedRun).status">Status {{ aiOutcome(selectedRun).status }}</span>
          </div>
          <details v-if="aiOutcome(selectedRun).preview || aiOutcome(selectedRun).blocks.length" class="ai-output-preview">
            <summary>Model output preview</summary>
            <pre v-if="aiOutcome(selectedRun).preview">{{ aiOutcome(selectedRun).preview }}</pre>
            <pre v-for="(block, blockIndex) in aiOutcome(selectedRun).blocks" :key="'selected-ai-block-' + blockIndex">{{ block }}</pre>
          </details>
        </div>
        <div v-if="dedupeReportRows(selectedRun).length" class="log-dedupe-section">
          <div class="section-title">Duplicate product report</div>
          <div v-if="dedupeSummaryRows(selectedRun).length" class="dedupe-summary">
            <span v-for="item in dedupeSummaryRows(selectedRun)" :key="item.key">
              <small>{{ item.label }}</small>
              <b>{{ item.value }}</b>
            </span>
          </div>
          <table class="dedupe-report-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>ASIN</th>
                <th>Kept id</th>
                <th>Dropped ids</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in dedupeReportRows(selectedRun)" :key="item.key">
                <td>{{ item.type }}</td>
                <td>{{ item.asin }}</td>
                <td>{{ item.keep }}</td>
                <td>{{ item.drop }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        </template>
        <pre class="log-body">{{ selectedRun.log || '(no output)' }}</pre>
        <div class="log-foot" v-if="selectedRun.stats && Object.keys(selectedRun.stats).length">
          <strong>Stats:</strong> {{ statsSummary(selectedRun.stats) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../config'
import realtime from '../../services/realtime'
import AdminTable from './shared/AdminTable.vue'
import OnboardingRunSummary from './OnboardingRunSummary.vue'

const ONBOARDING_JOB_KEYS = ['catalog-auto-onboard', 'catalog-coverage-plan', 'catalog-coverage-report']

export default {
  name: 'ScheduledJobs',
  components: {
    AdminTable,
    OnboardingRunSummary
  },
  data () {
    return {
      jobs: [],
      runs: [],
      optionValues: {},
      scheduleDrafts: {},
      selectedRun: null,
      error: null,
      notice: null,
      unsubscribeJobsLive: null,
      unsubscribeRunLive: null,
      unsubscribeLiveState: null,
      liveFallbackTimer: null,
      liveConnected: false,
      includeHidden: false,
      primaryJobKey: 'onboard-parts',
      detailJob: null,
      expandedRuns: {},
      runJobFilter: 'all',
      runStatusFilter: 'all',
      activeWorkspace: 'jobs',
      activeJobCategoryKey: 'parts-onboarding',
      jobCategoriesMeta: [],
      categoryOrder: ['parts-management', 'parts-quality', 'parts-onboarding', 'analytics', 'reports'],
      scopePreview: null,
      scopeLoading: false,
      scopeError: '',
      scopeTimer: null,
      scopePreviewLimit: 12,
      configSaving: {},
      justSavedKey: '',
      savedFlashTimer: null,
      startingRuns: {},
      testDeliveryStarting: {}
    }
  },
  computed: {
    displayJobs () {
      const primary = this.primaryJobKey
      // Pin the primary onboarding workflow to the front, keep backend order otherwise.
      return this.jobs.slice().sort((a, b) => {
        if (a.key === primary) return -1
        if (b.key === primary) return 1
        return 0
      })
    },
    jobCategories () {
      const meta = this.categoryMetaList()
      const groups = {}
      meta.forEach((def) => {
        groups[def.key] = Object.assign({}, def, { jobs: [], count: 0, activeCount: 0 })
      })
      this.displayJobs.forEach((job) => {
        const key = this.jobCategoryKey(job)
        if (!groups[key]) {
          const fallback = this.categoryMetaFromJob(job)
          groups[key] = Object.assign({}, fallback, { jobs: [], count: 0, activeCount: 0 })
          meta.push(groups[key])
        }
        const group = groups[key]
        group.jobs.push(job)
        group.count += 1
        if (this.isActive(job)) group.activeCount += 1
      })
      return meta
        .map((def) => groups[def.key])
        .filter((group) => group.count > 0)
        .sort((a, b) => this.categorySort(a.key, b.key))
    },
    activeCategory () {
      return this.jobCategories.find((group) => group.key === this.activeJobCategoryKey) ||
        this.jobCategories[0] ||
        { key: '', label: 'Jobs', eyebrow: 'Automation', description: '', jobs: [], count: 0, activeCount: 0 }
    },
    activeCategoryJobs () {
      return this.activeCategory && this.activeCategory.jobs ? this.activeCategory.jobs : []
    },
    decoratedRuns () {
      return (this.runs || []).map((run) => Object.assign({}, run, {
        errorText: run.error || '',
        result: this.resultModel(run.stats, run)
      }))
    },
    activeCategoryRunJobKeys () {
      const keys = {}
      this.activeCategoryJobs.forEach((job) => {
        if (job && job.key) keys[job.key] = true
      })
      return keys
    },
    filteredRuns () {
      return this.decoratedRuns.filter((run) => {
        if (this.runJobFilter !== 'all' && run.jobKey !== this.runJobFilter) return false
        if (this.runStatusFilter !== 'all' && run.status !== this.runStatusFilter) return false
        return true
      })
    },
    jobFilterOptions () {
      const seen = {}
      const opts = []
      ;(this.runs || []).forEach((run) => {
        if (run.jobKey && !seen[run.jobKey]) {
          seen[run.jobKey] = true
          opts.push({
            key: run.jobKey,
            name: this.jobName(run.jobKey) || this.prettyLabel(run.jobKey) || String(run.jobKey)
          })
        }
      })
      return opts.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    },
    selectedRunNote () {
      return this.selectedRun ? this.resultModel(this.selectedRun.stats).note : ''
    },
    jobColumns () {
      return [
        { key: 'job', label: 'Job', width: '300px', breakable: true, sortable: true, defaultDir: 'asc', sortAccessor: (r) => r.name || r.key },
        { key: 'status', label: 'Last status', width: '100px', nowrap: true, sortable: true, sortAccessor: (r) => this.jobDisplayStatus(r) },
        { key: 'schedule', label: 'Schedule', width: '145px', breakable: true, sortable: true, sortAccessor: (r) => r.enabled ? r.cron : '' },
        { key: 'lastrun', label: 'Last run', width: '125px', nowrap: true, sortable: true, sortAccessor: (r) => (r.lastRun && new Date(r.lastRun.startedAt).getTime()) || 0 },
        { key: 'result', label: 'Last result', width: '230px', breakable: true },
        { key: 'actions', label: '', width: '165px', align: 'right', nowrap: true }
      ]
    },
    runColumns () {
      return [
        { key: 'job', label: 'Job', width: '190px', breakable: true, sortable: true, defaultDir: 'asc', sortAccessor: (r) => this.jobName(r.jobKey) },
        { key: 'status', label: 'Status', width: '116px', nowrap: true, sortable: true, sortAccessor: (r) => r.status || '' },
        { key: 'trigger', label: 'Trigger', width: '100px', nowrap: true, sortable: true, sortAccessor: (r) => r.trigger || '' },
        { key: 'started', label: 'Started', width: '130px', nowrap: true, sortable: true, sortAccessor: (r) => new Date(r.startedAt).getTime() || 0 },
        { key: 'duration', label: 'Duration', width: '96px', nowrap: true },
        { key: 'result', label: 'Result', width: '44%', breakable: true },
        { key: 'actions', label: '', width: '86px', align: 'right', nowrap: true }
      ]
    }
  },
  mounted () {
    this.fetchAll()
    this.connectLiveJobs()
    // Deep link from a candidate ("View source run") opens that run's log.
    if (this.$route.query.run) {
      this.viewRun({ id: this.$route.query.run })
    }
  },
  beforeDestroy () {
    this.disconnectLiveJobs()
    if (this.scopeTimer) clearTimeout(this.scopeTimer)
    if (this.savedFlashTimer) clearTimeout(this.savedFlashTimer)
  },
  methods: {
    connectLiveJobs () {
      this.liveConnected = realtime.isConnected()
      this.unsubscribeLiveState = realtime.onStateChange((state, connected, detail) => {
        this.liveConnected = !!connected
        if (connected && state === 'open') {
          if (this.liveFallbackTimer) clearTimeout(this.liveFallbackTimer)
          this.liveFallbackTimer = null
          this.error = null
          realtime.refresh('jobs:all')
        } else {
          if (state === 'error') this.error = realtime.errorMessage(detail)
          this.scheduleLiveFallback(500)
        }
      })
      this.unsubscribeJobsLive = realtime.subscribe('jobs:all', {
        'job:snapshot': this.handleJobsSnapshot,
        'job:started': this.handleJobRunEvent,
        'job:progress': this.handleJobProgress,
        'job:finished': this.handleJobRunEvent,
        'job:config': this.handleJobConfig,
        'job:cleanup': this.handleJobCleanup,
        'job:error': this.handleJobRealtimeError
      })
      this.scheduleLiveFallback(5000)
    },
    disconnectLiveJobs () {
      if (this.unsubscribeJobsLive) this.unsubscribeJobsLive()
      if (this.unsubscribeRunLive) this.unsubscribeRunLive()
      if (this.unsubscribeLiveState) this.unsubscribeLiveState()
      if (this.liveFallbackTimer) clearTimeout(this.liveFallbackTimer)
      this.unsubscribeJobsLive = null
      this.unsubscribeRunLive = null
      this.unsubscribeLiveState = null
      this.liveFallbackTimer = null
    },
    refreshLive () {
      this.fetchAll()
      realtime.refresh('jobs:all')
      if (this.selectedRun && this.selectedRun.id) realtime.refresh(`jobs:${this.selectedRun.id}`)
    },
    scheduleLiveFallback (delay) {
      if (this.liveFallbackTimer) clearTimeout(this.liveFallbackTimer)
      this.liveFallbackTimer = null
      if (realtime.isConnected()) return
      this.liveFallbackTimer = setTimeout(async () => {
        this.liveFallbackTimer = null
        if (realtime.isConnected()) return
        await this.fetchAll()
        if (this.selectedRun && this.selectedRun.id) await this.refreshSelectedRunDetail(this.selectedRun.id)
        if (!realtime.isConnected()) this.scheduleLiveFallback(5000)
      }, delay || 5000)
    },
    subscribeSelectedRun () {
      if (this.unsubscribeRunLive) this.unsubscribeRunLive()
      this.unsubscribeRunLive = null
      if (!this.selectedRun || !this.selectedRun.id) return
      const topic = `jobs:${this.selectedRun.id}`
      this.unsubscribeRunLive = realtime.subscribe(topic, {
        'job:snapshot': this.handleSelectedRunSnapshot,
        'job:progress': this.handleSelectedRunProgress,
        'job:log': this.handleSelectedRunLog,
        'job:finished': this.handleSelectedRunEvent,
        'job:error': this.handleJobRealtimeError
      })
      if (realtime.isConnected()) realtime.refresh(topic)
    },
    closeSelectedRun () {
      this.selectedRun = null
      if (this.unsubscribeRunLive) this.unsubscribeRunLive()
      this.unsubscribeRunLive = null
    },
    handleJobsSnapshot (payload) {
      const data = payload || {}
      if (Array.isArray(data.runs)) this.mergeRuns(data.runs)
      if (Array.isArray(data.jobs)) this.mergeJobs(data.jobs)
      if (data.job) this.mergeJob(data.job)
      if (data.run) this.mergeRun(data.run)
    },
    handleJobRunEvent (payload) {
      const data = payload || {}
      const run = this.runFromEvent(data)
      if (run) this.mergeRun(run)
      if (data.job) this.mergeJob(data.job)
    },
    handleJobProgress (payload) {
      const data = payload || {}
      const run = this.runFromEvent(data)
      if (run) {
        if (!this.isTerminalRunId(run.id)) this.mergeRun(run)
        return
      }
      const id = data.runId || data.run_id || data.id
      if (!id || this.isTerminalRunId(id)) return
      this.patchRunById(id, {
        stats: data.stats || data.progressStats,
        progress: data.progress,
        progressPercent: data.progressPercent || data.percent,
        logLength: data.logLength || data.log_length,
        status: data.status || 'running'
      })
    },
    handleJobLog (payload) {
      const data = payload || {}
      const id = data.runId || data.run_id || data.id || (data.run && data.run.id)
      const append = data.logAppend || data.log_append || data.line || data.message || ''
      if (append) this.appendLogById(id, append)
      const run = this.runFromEvent(data)
      if (run && !this.isTerminalRunId(run.id)) this.mergeRun(run)
    },
    handleJobConfig (payload) {
      const data = payload || {}
      if (data.job) this.mergeJob(data.job)
    },
    handleJobCleanup (payload) {
      const runs = Array.isArray(payload && payload.runs) ? payload.runs : []
      runs.forEach((run) => this.mergeRun(Object.assign({ status: 'stopped' }, run)))
    },
    handleJobRealtimeError (payload) {
      const data = payload || {}
      this.error = data.message || data.reason || realtime.errorMessage(data)
    },
    handleSelectedRunSnapshot (payload) {
      const run = this.runFromEvent(payload || {})
      if (!run) return
      this.selectedRun = this.mergeSelectedRunSnapshot(run)
      this.mergeRun(run)
    },
    handleSelectedRunEvent (payload) {
      const run = this.runFromEvent(payload || {})
      if (!run) return
      this.selectedRun = this.normalizeRun(Object.assign({}, this.selectedRun || {}, run))
      this.mergeRun(run)
      if (this.isTerminalRun(run)) this.refreshSelectedRunDetail(run.id)
    },
    handleSelectedRunProgress (payload) {
      const data = payload || {}
      if (!this.selectedRun || this.isTerminalRun(this.selectedRun)) return
      const patch = this.runFromEvent(data) || {
        id: data.runId || data.run_id || data.id || this.selectedRun.id,
        stats: data.stats || data.progressStats,
        progress: data.progress,
        progressPercent: data.progressPercent || data.percent,
        logLength: data.logLength || data.log_length,
        status: data.status || this.selectedRun.status || 'running'
      }
      this.selectedRun = this.normalizeRun(Object.assign({}, this.selectedRun, patch))
      this.mergeRun(patch)
    },
    handleSelectedRunLog (payload) {
      this.handleJobLog(payload)
    },
    runFromEvent (payload) {
      if (!payload || typeof payload !== 'object') return null
      return payload.run || payload.jobRun || payload.job_run ||
        (payload.data && (payload.data.run || payload.data.jobRun || payload.data.job_run)) || null
    },
    mergeSelectedRunSnapshot (incoming) {
      const current = this.selectedRun || {}
      const next = Object.assign({}, current, incoming || {})
      const currentLog = typeof current.log === 'string' ? current.log : ''
      const incomingLog = incoming && typeof incoming.log === 'string' ? incoming.log : ''
      if (currentLog.length > incomingLog.length) next.log = currentLog
      return this.normalizeRun(next)
    },
    async refreshSelectedRunDetail (id) {
      if (!id || !this.selectedRun || String(this.selectedRun.id) !== String(id)) return
      try {
        const { data } = await axios.get(`${apiDomain}jobs/runs/${encodeURIComponent(id)}`, { headers: this.authHeaders() })
        const run = this.runFromEvent(data || {})
        if (run && this.selectedRun && String(this.selectedRun.id) === String(id)) {
          this.selectedRun = this.mergeSelectedRunSnapshot(run)
          this.mergeRun(run)
        }
      } catch (error) {
        this.error = this.errMsg(error)
      }
    },
    mergeJobs (jobs) {
      (jobs || []).forEach((job) => this.mergeJob(job))
    },
    mergeJob (incoming) {
      if (!incoming || !incoming.key) return
      const idx = this.jobs.findIndex((job) => job.key === incoming.key)
      // Realtime run events only include mutable job state (lastStatus,
      // lastRunAt, lastRunId). Merge that patch into the catalog definition
      // before normalizing so fields such as category and description survive
      // a completed run.
      const source = idx >= 0 ? Object.assign({}, this.jobs[idx], incoming) : incoming
      const normalized = this.normalizeJobs([source])[0]
      if (idx >= 0) {
        this.$set(this.jobs, idx, normalized)
      } else {
        this.jobs.push(normalized)
      }
      if (this.detailJob && this.detailJob.key === normalized.key) {
        this.detailJob = Object.assign({}, this.detailJob, normalized)
      }
      this.ensureActiveCategory()
    },
    mergeRuns (runs) {
      (runs || []).forEach((run) => this.mergeRun(run))
    },
    mergeRun (incoming) {
      if (!incoming) return
      const run = this.normalizeRun(incoming)
      const id = run && run.id
      if (!id) return
      const idx = (this.runs || []).findIndex((item) => String(item.id) === String(id))
      if (idx >= 0) {
        this.$set(this.runs, idx, Object.assign({}, this.runs[idx], run))
      } else {
        this.runs = [run].concat(this.runs || []).slice(0, 30)
      }
      this.applyRunSnapshot(run.jobKey, run)
      if (this.selectedRun && String(this.selectedRun.id) === String(id)) {
        this.selectedRun = this.mergeSelectedRunSnapshot(run)
      }
    },
    patchRunById (id, patch) {
      const clean = {}
      Object.keys(patch || {}).forEach((key) => {
        if (patch[key] !== undefined && patch[key] !== null) clean[key] = patch[key]
      })
      const idx = (this.runs || []).findIndex((run) => String(run.id) === String(id))
      if (idx >= 0) {
        const next = this.normalizeRun(Object.assign({}, this.runs[idx], clean))
        this.$set(this.runs, idx, next)
        this.applyRunSnapshot(next.jobKey, next)
      }
      if (this.selectedRun && String(this.selectedRun.id) === String(id) && !this.isTerminalRun(this.selectedRun)) {
        this.selectedRun = this.normalizeRun(Object.assign({}, this.selectedRun, clean))
      }
    },
    appendLogById (id, append) {
      if (!id || !append) return
      const idx = (this.runs || []).findIndex((run) => String(run.id) === String(id))
      if (idx >= 0) {
        const run = Object.assign({}, this.runs[idx], { log: `${this.runs[idx].log || ''}${append}` })
        this.$set(this.runs, idx, this.normalizeRun(run))
      }
      if (this.selectedRun && String(this.selectedRun.id) === String(id)) {
        this.selectedRun = this.normalizeRun(Object.assign({}, this.selectedRun, {
          log: `${this.selectedRun.log || ''}${append}`
        }))
      }
    },
    isTerminalRunId (id) {
      const run = (this.runs || []).find((item) => String(item.id) === String(id))
      return this.isTerminalRun(run)
    },
    isTerminalRun (run) {
      if (!run || !run.status) return false
      return !this.isRunning(run)
    },
    runRowClass (run) {
      return ['row-' + run.status, { 'is-expanded': this.isExpanded(run) }]
    },
    jobRowClass (job) {
      return { 'job-row-featured': job.key === this.primaryJobKey, 'job-row-live': this.isActive(job) }
    },
    jobCardClass (job) {
      return [
        `cat-card-${this.jobCategoryKey(job)}`,
        {
          'is-featured': job && job.key === this.primaryJobKey,
          'is-active': this.isActive(job),
          'is-hidden': job && job.hidden
        }
      ]
    },
    selectJobCategory (key) {
      this.activeJobCategoryKey = key
      this.runJobFilter = 'all'
    },
    isDedupeJob (source) {
      const key = source && (source.key || source.jobKey || source.job_key)
      return key === 'dedupe-catalog-products'
    },
    dedupeMode (job) {
      if (!this.isDedupeJob(job)) return ''
      const key = job && (job.key || job.jobKey || job.job_key)
      const values = (key && this.optionValues[key]) || {}
      return String(values.mode || 'report').toLowerCase()
    },
    normalizeTestDelivery (source, jobKey) {
      if (!source || typeof source !== 'object' || !jobKey) return null
      const endpoint = String(source.endpoint || '').trim().replace(/\/+$/, '')
      const method = String(source.method || 'POST').trim().toUpperCase()
      const expectedEndpoint = `/api/jobs/${jobKey}/test`
      if (method !== 'POST' || endpoint !== expectedEndpoint) return null
      return {
        label: source.label || 'Send test to me',
        method,
        endpoint,
        recipient: source.recipient || 'authenticated_admin'
      }
    },
    testDeliveryFor (job) {
      if (!job) return null
      return this.normalizeTestDelivery(job.testDelivery || job.test_delivery, job.key)
    },
    testDeliveryButtonLabel (job) {
      if (job && this.testDeliveryStarting[job.key]) return 'Starting test...'
      if (job && this.isActive(job)) return 'Report running...'
      const delivery = this.testDeliveryFor(job)
      return delivery ? delivery.label : 'Send test to me'
    },
    testDeliveryOptions (job) {
      const options = this.runOptions(job)
      const configured = Number(options.windowHours)
      return {
        windowHours: Number.isFinite(configured) && configured > 0 ? configured : 24
      }
    },
    testDeliveryUrl (job) {
      const delivery = this.testDeliveryFor(job)
      if (!delivery) return ''
      const base = String(apiDomain || '').replace(/\/+$/, '')
      return base + delivery.endpoint.replace(/^\/api(?=\/)/, '')
    },
    runButtonClass (job) {
      const classes = {
        'is-live': this.isActive(job),
        'is-starting': this.jobRunStatus(job) === 'starting'
      }
      if (!this.isDedupeJob(job)) return classes
      const mode = this.dedupeMode(job)
      return Object.assign(classes, {
        danger: mode === 'delete',
        warning: mode === 'archive'
      })
    },
    runButtonLabel (job) {
      const status = this.jobRunStatus(job)
      if (status === 'starting') return 'Starting...'
      if (status === 'queued') return 'Queued...'
      if (status === 'running') return 'Running...'
      return 'Run Now'
    },
    jobDisplayStatus (job) {
      const active = this.activeRunForJob(job)
      if (active && active.status) return active.status
      return (job && job.lastStatus) || 'none'
    },
    jobRunStatus (job) {
      const active = this.activeRunForJob(job)
      return active && active.status ? String(active.status).toLowerCase() : ''
    },
    activeRunForJob (job, includeStarting) {
      const key = job && job.key
      if (!key) return null
      const activeRun = (this.runs || []).find((run) => this.runJobKey(run) === key && this.isRunning(run))
      if (activeRun) return activeRun
      if (job.lastRun && this.isRunning(job.lastRun)) return job.lastRun
      return includeStarting === false ? null : (this.startingRuns[key] || null)
    },
    runJobKey (run) {
      return run ? String(run.jobKey || run.job_key || run.job || '') : ''
    },
    dedupeMutationWarning (job) {
      if (!this.isDedupeJob(job)) return ''
      const mode = this.dedupeMode(job)
      if (mode === 'archive') {
        return 'This will mark duplicate products unavailable. Run report first to inspect the duplicate groups before changing catalog rows.'
      }
      if (mode === 'delete') {
        return 'This permanently deletes duplicate part rows and their spec, compatibility, and price rows. Run report first and review the scope before deleting.'
      }
      return ''
    },
    confirmDedupeRun (job, options) {
      if (!this.isDedupeJob(job)) return true
      const mode = String((options && options.mode) || this.dedupeMode(job) || 'report').toLowerCase()
      if (mode === 'report') return true
      const affected = this.dedupeScopeAffectedRows(this.scopePreview)
      const groupCount = this.dedupeScopeGroupCount(this.scopePreview)
      const rowsText = affected !== null ? `${affected} duplicate part row${affected === 1 ? '' : 's'}` : 'duplicate part rows'
      const groupsText = groupCount !== null ? ` across ${groupCount} group${groupCount === 1 ? '' : 's'}` : ''
      const prefix = mode === 'delete'
        ? `This will permanently delete ${rowsText}${groupsText}. This cannot be undone.`
        : `This will archive ${rowsText}${groupsText} by marking duplicates unavailable.`
      const dryRun = 'Run report first if you have not reviewed the duplicate groups.'
      return window.confirm(`${prefix}\n\n${dryRun}\n\nContinue?`)
    },
    dedupeScopeAffectedRows (scope) {
      if (!scope || typeof scope !== 'object') return null
      const direct = scope.duplicateParts || scope.duplicate_parts || scope.dropCount || scope.drop_count
      if (direct !== undefined && direct !== null && direct !== '') return Number(direct)
      const rows = this.dedupeReportRows({ stats: scope })
      if (rows.length) {
        return rows.reduce((sum, row) => {
          const parts = String(row.drop || '').split(',').map((item) => item.trim()).filter(Boolean)
          return sum + parts.length
        }, 0)
      }
      if (Array.isArray(scope.items)) return scope.items.length
      if (scope.total !== undefined && scope.total !== null && scope.total !== '') return Number(scope.total)
      return null
    },
    dedupeScopeGroupCount (scope) {
      if (!scope || typeof scope !== 'object') return null
      const direct = scope.duplicateGroups || scope.duplicate_groups || scope.groups
      if (typeof direct === 'number') return direct
      const rows = this.dedupeReportRows({ stats: scope })
      if (rows.length) return rows.length
      return null
    },
    jobCategoryMeta (job) {
      const key = this.jobCategoryKey(job)
      return this.categoryMetaList().find((def) => def.key === key) ||
        this.categoryMetaFromJob(job) ||
        { key, label: this.prettyLabel(key || 'Jobs'), eyebrow: 'Automation', description: '' }
    },
    jobCategoryKey (job) {
      if (!job) return ''
      const meta = job.categoryMeta || job.category_meta || {}
      return String(job.category || meta.key || meta.category || '').toLowerCase()
    },
    runCategoryKey (run) {
      if (!run) return ''
      if (run.category) return String(run.category).toLowerCase()
      const job = this.jobs.find((item) => item.key === run.jobKey)
      return this.jobCategoryKey(job)
    },
    categoryMetaList () {
      return this.normalizeCategories(this.jobCategoriesMeta)
    },
    categorySort (a, b) {
      const ai = this.categoryOrder.indexOf(a)
      const bi = this.categoryOrder.indexOf(b)
      if (ai !== -1 || bi !== -1) {
        if (ai === -1) return 1
        if (bi === -1) return -1
        return ai - bi
      }
      return String(a || '').localeCompare(String(b || ''))
    },
    categoryMetaFromJob (job) {
      const key = this.jobCategoryKey(job)
      const meta = (job && (job.categoryMeta || job.category_meta)) || {}
      return {
        key,
        label: job && (job.categoryLabel || job.category_label) || meta.label || meta.name || this.prettyLabel(key || 'Jobs'),
        eyebrow: meta.eyebrow || meta.group || 'Automation',
        description: meta.description || meta.summary || ''
      }
    },
    authHeaders () {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.usertoken}`
      }
    },
    unwrapCategories (source) {
      if (!source) return null
      return source.categories || source.jobCategories || source.data || null
    },
    normalizeCategories (categories) {
      const source = categories || []
      const rows = Array.isArray(source)
        ? source
        : Object.keys(source).map((key) => Object.assign({ key }, source[key] || {}))
      return rows.map((category) => {
        const item = category || {}
        const key = String(item.key || item.category || item.id || '').toLowerCase()
        return {
          key,
          label: item.label || item.name || item.categoryLabel || this.prettyLabel(key || 'Jobs'),
          eyebrow: item.eyebrow || item.group || item.kind || 'Automation',
          description: item.description || item.summary || '',
          meta: item.meta || item.categoryMeta || item.category_meta || {}
        }
      }).filter((category) => category.key)
    },
    ensureActiveCategory () {
      const groups = this.jobCategories
      if (!groups.length) return
      if (!groups.some((group) => group.key === this.activeJobCategoryKey)) {
        this.activeJobCategoryKey = groups[0].key
      }
    },
    runOptions (job) {
      const values = Object.assign({}, (job && this.optionValues[job.key]) || {})
      const optionKeys = this.jobOptions(job).map((opt) => opt.key)
      if (optionKeys.indexOf('previewLimit') === -1) delete values.previewLimit
      return values
    },
    // The option keys that make up a job's persisted config (everything the run
    // settings expose, minus the preview-only knob).
    configKeys (job) {
      return this.jobOptions(job).map((opt) => opt.key).filter((key) => key !== 'previewLimit')
    },
    // Normalize a source bag down to just the config keys, filling gaps with the
    // option default so saved-vs-current comparisons line up.
    normConfig (job, source) {
      const out = {}
      const opts = this.jobOptions(job)
      this.configKeys(job).forEach((key) => {
        let value = source ? source[key] : undefined
        if (value === undefined) {
          const opt = opts.find((o) => o.key === key)
          value = opt ? opt.default : ''
        }
        out[key] = value
      })
      return out
    },
    savedConfigFor (job) {
      return this.normConfig(job, (job && job.config) || {})
    },
    currentConfigFor (job) {
      return this.normConfig(job, (job && this.optionValues[job.key]) || {})
    },
    // True when the visible run settings differ from what's persisted as config
    // (i.e. what an automatic run would actually use).
    configDirty (job) {
      if (!job) return false
      const current = this.currentConfigFor(job)
      const saved = this.savedConfigFor(job)
      return this.configKeys(job).some((key) => String(current[key]) !== String(saved[key]))
    },
    configStateLabel (job) {
      if (this.configSaving[job.key]) return 'Saving…'
      if (this.configDirty(job)) return 'Unsaved changes'
      if (this.justSavedKey === job.key) return 'Saved ✓'
      return 'Saved'
    },
    configStateClass (job) {
      if (this.configDirty(job)) return 'is-dirty'
      if (this.justSavedKey === job.key) return 'is-flash'
      return 'is-saved'
    },
    // Human-readable chips for the run config, used in the scheduled summary and
    // on the job card so each scheduled job shows the params it will run with.
    configSummaryChips (job) {
      if (!job) return []
      const values = this.optionValues[job.key] || {}
      return this.jobOptions(job)
        .filter((opt) => opt.key !== 'previewLimit' && !this.isNoteOpt(opt) && opt.type !== 'textarea')
        .map((opt) => {
          const raw = values[opt.key]
          const value = raw === undefined ? opt.default : raw
          const def = opt.default
          return {
            key: opt.key,
            label: this.prettyLabel(opt.label || opt.key),
            value: this.formatConfigValue(value),
            isDefault: String(value) === String(def === undefined ? '' : def)
          }
        })
        .filter((chip) => chip.value !== '')
    },
    formatConfigValue (value) {
      if (typeof value === 'boolean') return value ? 'On' : 'Off'
      if (value === undefined || value === null) return ''
      return String(value)
    },
    async saveConfig (job, opts) {
      if (!job || !job.key) return
      const silent = opts && opts.silent
      const config = this.runOptions(job)
      this.$set(this.configSaving, job.key, true)
      try {
        await axios.post(`${apiDomain}jobs/${job.key}`,
          { config }, { headers: this.authHeaders() })
        // Mirror the saved config onto the local job so the dirty check clears
        // immediately without waiting on a refetch (which would drop the ref).
        this.$set(job, 'config', config)
        this.error = null
        if (!silent) {
          this.justSavedKey = job.key
          if (this.savedFlashTimer) clearTimeout(this.savedFlashTimer)
          this.savedFlashTimer = setTimeout(() => {
            if (this.justSavedKey === job.key) this.justSavedKey = ''
          }, 2500)
        }
        return true
      } catch (e) {
        this.error = this.errMsg(e)
        return false
      } finally {
        this.$set(this.configSaving, job.key, false)
      }
    },
    // ---- next-run estimate from the cron expression ----
    cronFieldMatch (field, value) {
      if (!field || field === '*') return true
      if (field.indexOf('*/') === 0) {
        const step = parseInt(field.slice(2), 10)
        return step > 0 && value % step === 0
      }
      return field.split(',').some((part) => parseInt(part, 10) === value)
    },
    cronNextRun (cron) {
      if (!cron) return null
      const parts = String(cron).trim().split(/\s+/)
      if (parts.length !== 5) return null
      const [min, hr, dom, mon, dow] = parts
      const d = new Date()
      d.setSeconds(0, 0)
      d.setMinutes(d.getMinutes() + 1)
      // Scan minute-by-minute up to two weeks — covers every schedule we emit.
      for (let i = 0; i < 14 * 24 * 60; i += 1) {
        if (
          this.cronFieldMatch(min, d.getMinutes()) &&
          this.cronFieldMatch(hr, d.getHours()) &&
          this.cronFieldMatch(dom, d.getDate()) &&
          this.cronFieldMatch(mon, d.getMonth() + 1) &&
          this.cronFieldMatch(dow, d.getDay())
        ) return new Date(d)
        d.setMinutes(d.getMinutes() + 1)
      }
      return null
    },
    nextRunHint (job) {
      if (!job || !job.enabled || !job.cron) return ''
      const next = this.cronNextRun(job.cron)
      if (!next) return ''
      return `${this.fmtFutureRel(next)} · ${this.fmtTime(next)}`
    },
    fmtFutureRel (date) {
      const s = Math.round((date.getTime() - Date.now()) / 1000)
      if (s < 60) return 'in under a minute'
      const m = Math.round(s / 60)
      if (m < 60) return `in ${m}m`
      const h = Math.round(m / 60)
      if (h < 24) return `in ${h}h`
      const days = Math.round(h / 24)
      return `in ${days}d`
    },
    scheduleSummaryLine (job) {
      const draft = this.scheduleDrafts[job.key]
      if (draft && draft.custom) return `on cron ${job.cron}`
      if (!draft) return this.cronHint(job.cron).toLowerCase()
      const unit = this.scheduleUnitLabel(draft)
      return `every ${draft.every} ${unit}`
    },
    jobCanRun (job) {
      if (!job) return false
      if (this.detailJob && job.key === this.detailJob.key && this.scopePreview && this.scopePreview.canRun === false) {
        return false
      }
      const scope = job.scope || {}
      return scope.canRun !== false
    },
    closeDetail () {
      this.detailJob = null
      this.scopePreview = null
      this.scopeError = ''
      this.scopeLoading = false
      if (this.scopeTimer) clearTimeout(this.scopeTimer)
    },
    queueScopePreview (job) {
      if (!job || !job.key) return
      if (this.scopeTimer) clearTimeout(this.scopeTimer)
      this.scopeTimer = setTimeout(() => this.fetchScopePreview(job), 350)
    },
    async fetchScopePreview (job) {
      if (!job || !job.key) return
      this.scopeLoading = true
      this.scopeError = ''
      try {
        const params = this.scopeQuery(job)
        const suffix = params ? `?${params}` : ''
        const { data } = await axios.get(`${apiDomain}jobs/${encodeURIComponent(job.key)}/scope${suffix}`,
          { headers: this.authHeaders() })
        this.scopePreview = this.normalizeScope(data && (data.scope || data))
      } catch (e) {
        this.scopePreview = null
        this.scopeError = this.errMsg(e)
      } finally {
        this.scopeLoading = false
      }
    },
    scopeQuery (job) {
      const params = new URLSearchParams()
      const values = (job && this.optionValues[job.key]) || {}
      Object.keys(values).forEach((key) => {
        const value = values[key]
        if (value === undefined || value === null || value === '') return
        params.set(key, value)
      })
      params.set('previewLimit', String(this.scopePreviewLimit))
      return params.toString()
    },
    jobsUrl () {
      return `${apiDomain}jobs${this.includeHidden ? '?includeHidden=true' : ''}`
    },
    async toggleHidden (ev) {
      this.includeHidden = ev.target.checked
      await this.fetchAll()
    },
    async fetchAll () {
      try {
        const detailKey = this.detailJob && this.detailJob.key
        const [jobsRes, runsRes, categoriesRes] = await Promise.all([
          axios.get(this.jobsUrl(), { headers: this.authHeaders() }),
          axios.get(`${apiDomain}jobs/runs?limit=25`, { headers: this.authHeaders() }),
          axios.get(`${apiDomain}jobs/categories`, { headers: this.authHeaders() }).catch(() => null)
        ])
        this.jobs = this.normalizeJobs(jobsRes.data.jobs || jobsRes.data)
        if (detailKey) {
          const refreshedDetail = this.jobs.find((job) => job.key === detailKey)
          if (refreshedDetail) this.detailJob = refreshedDetail
        }
        const categoryPayload = this.unwrapCategories(categoriesRes ? categoriesRes.data : null)
        const jobsCategoryPayload = this.unwrapCategories(jobsRes.data)
        const categoryRows = this.normalizeCategories(categoryPayload)
        const jobsCategoryRows = this.normalizeCategories(jobsCategoryPayload || [])
        this.jobCategoriesMeta = categoryRows.length ? categoryRows : jobsCategoryRows
        this.runs = this.normalizeRuns(runsRes.data.runs || runsRes.data)
        this.initOptionValues()
        this.initScheduleDrafts()
        this.ensureActiveCategory()
        this.error = null
      } catch (e) {
        this.error = this.errMsg(e)
      }
    },
    initOptionValues () {
      const next = {}
      for (const job of this.jobs) {
        const existing = this.optionValues[job.key] || {}
        const vals = {}
        for (const opt of this.jobOptions(job)) {
          const stored = job.config && job.config[opt.key]
          vals[opt.key] = existing[opt.key] !== undefined
            ? existing[opt.key]
            : (stored !== undefined ? stored : opt.default)
        }
        next[job.key] = vals
      }
      this.optionValues = next
    },
    initScheduleDrafts () {
      const next = {}
      for (const job of this.jobs) {
        const cron = job.cron || ''
        const existing = this.scheduleDrafts[job.key]
        next[job.key] = existing && existing.sourceCron === cron
          ? existing
          : this.scheduleFromCron(cron)
      }
      this.scheduleDrafts = next
    },
    runFromResponse (payload) {
      if (!payload || typeof payload !== 'object') return null
      const candidate = payload.run ||
        payload.jobRun ||
        payload.job_run ||
        payload.existingRun ||
        payload.existing_run ||
        payload.activeRun ||
        payload.active_run ||
        (payload.data && (payload.data.run || payload.data.jobRun || payload.data.job_run)) ||
        (payload.data && (payload.data.id || payload.data.runId || payload.data.run_id) ? payload.data : null) ||
        (payload.id || payload.runId || payload.run_id ? payload : null)
      return candidate ? this.normalizeRun(candidate) : null
    },
    async sendTestDelivery (job) {
      const delivery = this.testDeliveryFor(job)
      if (!job || !delivery || this.isActive(job) || this.testDeliveryStarting[job.key]) return

      const options = this.testDeliveryOptions(job)
      const startingRun = this.makeStartingRun(job, Object.assign({}, options, { testMode: true }))
      this.$set(this.startingRuns, job.key, startingRun)
      this.$set(this.testDeliveryStarting, job.key, true)
      this.error = null
      this.notice = null

      try {
        const response = await axios({
          method: delivery.method,
          url: this.testDeliveryUrl(job),
          data: { options },
          headers: this.authHeaders()
        })
        const payload = response && response.data ? response.data : {}
        const run = this.runFromResponse(payload)
        if (run && run.id) {
          this.$delete(this.startingRuns, job.key)
          this.applyRunSnapshot(job.key, run)
          this.runs = [run].concat((this.runs || []).filter((item) => item.id !== run.id)).slice(0, 25)
        }

        await this.fetchAll()

        if (payload.started === false) {
          this.notice = payload.message ||
            'The Daily Operations Report is already running. Tracking the existing run.'
        } else {
          this.notice = 'Test report queued. It will be sent only to your signed-in admin email.'
        }

        if (!this.activeRunForJob(job, false)) this.$delete(this.startingRuns, job.key)
      } catch (e) {
        this.$delete(this.startingRuns, job.key)
        this.error = this.errMsg(e)
      } finally {
        this.$delete(this.testDeliveryStarting, job.key)
      }
    },
    async runJob (job) {
      if (!this.jobCanRun(job) || this.isActive(job)) return
      const options = this.runOptions(job)
      if (!this.confirmDedupeRun(job, options)) return
      const startingRun = this.makeStartingRun(job, options)
      this.$set(this.startingRuns, job.key, startingRun)
      this.error = null
      this.notice = null
      try {
        const response = await axios.post(`${apiDomain}jobs/${job.key}/run`,
          { options },
          { headers: this.authHeaders() })
        const run = response && response.data ? this.normalizeRun(response.data.run) : null
        if (run && run.id) {
          this.$delete(this.startingRuns, job.key)
          this.applyRunSnapshot(job.key, run)
          this.runs = [run].concat((this.runs || []).filter((item) => item.id !== run.id)).slice(0, 25)
        }
        await this.fetchAll()
        if (!this.activeRunForJob(job, false)) this.$delete(this.startingRuns, job.key)
      } catch (e) {
        this.$delete(this.startingRuns, job.key)
        this.error = this.errMsg(e)
      }
    },
    makeStartingRun (job, options) {
      return {
        id: `starting-${job.key}-${Date.now()}`,
        jobKey: job.key,
        jobName: job.name || job.key,
        status: 'starting',
        trigger: 'manual',
        startedAt: new Date().toISOString(),
        stats: Object.assign({}, options || {})
      }
    },
    applyRunSnapshot (jobKey, run) {
      if (!jobKey || !run) return
      const apply = (job) => {
        if (!job || job.key !== jobKey) return
        this.$set(job, 'lastRun', run)
        this.$set(job, 'lastStatus', run.status || job.lastStatus)
        this.$set(job, 'lastRunAt', run.startedAt || job.lastRunAt)
      }
      ;(this.jobs || []).forEach(apply)
      apply(this.detailJob)
    },
    normalizeJobs (jobs) {
      return (jobs || []).filter((job) => job && typeof job === 'object').map((job) => {
        const key = this.scalarText(job.key || job.jobKey || job.job_key, '')
        return Object.assign({}, job, {
          key,
          name: this.scalarText(job.name || job.label, key ? this.prettyLabel(key) : 'Unnamed job'),
          category: job.category || job.category_key || '',
          categoryLabel: job.categoryLabel || job.category_label || (job.categoryMeta && job.categoryMeta.label) || '',
          categoryMeta: job.categoryMeta || job.category_meta || {},
          scope: this.normalizeScope(job.scope || job.scopeDescriptor || job.scope_descriptor || null),
          testDelivery: this.normalizeTestDelivery(job.testDelivery || job.test_delivery, job.key),
          options: Array.isArray(job.options) ? job.options : [],
          aiPrompts: this.normalizeAiPrompts(job.aiPrompts || job.ai_prompts || (job.ai && job.ai.prompts)),
          lastRun: job.lastRun ? this.normalizeRun(job.lastRun) : job.lastRun
        })
      })
    },
    normalizeRuns (runs) {
      return (runs || []).map((run) => this.normalizeRun(run))
    },
    normalizeRun (run) {
      if (!run) return run
      const ai = run.ai && typeof run.ai === 'object' ? run.ai : {}
      return Object.assign({}, run, {
        ai,
        jobKey: run.jobKey || run.job_key || run.job || '',
        jobName: run.jobName || run.job_name || '',
        category: run.category || '',
        categoryLabel: run.categoryLabel || run.category_label || '',
        scope: this.normalizeScope(run.scope || null),
        aiPrompts: this.normalizeAiPrompts(run.aiPrompts || run.ai_prompts || ai.prompts),
        aiPromptUsage: this.normalizeAiPromptUsage(run.aiPromptUsage || run.ai_prompt_usage || ai.usage),
        aiProvider: run.aiProvider || run.ai_provider || ai.primaryProvider || ai.primary_provider || '',
        aiModel: run.aiModel || run.ai_model || ai.primaryModel || ai.primary_model || ''
      })
    },
    normalizeScope (scope) {
      if (!scope || typeof scope !== 'object') return scope
      const rawPlan = scope.plan && typeof scope.plan === 'object' ? scope.plan : null
      const items = Array.isArray(scope.items)
        ? scope.items.map((item) => {
          if (!item || typeof item !== 'object') return item
          return Object.assign({}, item, {
            selected: item.selected !== undefined ? this.boolFrom(item.selected) : item.selected,
            reportOnly: item.reportOnly !== undefined
              ? this.boolFrom(item.reportOnly)
              : this.boolFrom(item.report_only, false)
          })
        })
        : scope.items
      return Object.assign({}, scope, {
        canRun: scope.canRun !== undefined ? scope.canRun : scope.can_run,
        canPreview: scope.canPreview !== undefined ? scope.canPreview : scope.can_preview,
        byType: scope.byType || scope.by_type,
        byStatus: scope.byStatus || scope.by_status,
        descriptor: scope.descriptor || scope.scopeDescriptor || scope.scope_descriptor || {},
        plan: rawPlan
          ? Object.assign({}, rawPlan, {
            willDispatch: rawPlan.willDispatch !== undefined ? rawPlan.willDispatch : rawPlan.will_dispatch,
            actionable: this.boolFrom(rawPlan.actionable, rawPlan.is_actionable, false)
          })
          : null,
        items
      })
    },
    normalizeAiPrompts (prompts) {
      if (!Array.isArray(prompts)) return []
      return prompts.map((prompt) => {
        const item = prompt && typeof prompt === 'object' ? prompt : { key: prompt, name: prompt }
        const key = this.scalarText(item.key || item.promptKey || item.prompt_key || item.name, '')
        const provider = this.scalarText(item.provider || item.defaultProvider || item.default_provider, '')
        const model = this.scalarText(item.model || item.defaultModel || item.default_model, '')
        const webSearchEnabled = this.boolFrom(item.webSearchEnabled, item.web_search_enabled, item.mode === 'web_search')
        return {
          key,
          name: this.scalarText(item.name, key),
          provider,
          model,
          mode: this.scalarText(item.mode, '') || (webSearchEnabled ? 'web_search' : 'standard'),
          webSearchEnabled,
          version: this.scalarText(item.version || item.promptVersion || item.prompt_version, ''),
          isOverridden: item.isOverridden !== undefined ? !!item.isOverridden : !!item.is_overridden,
          requiresSeed: item.requiresSeed !== undefined ? !!item.requiresSeed : !!item.requires_seed
        }
      }).filter((prompt) => prompt.key)
    },
    normalizeAiPromptUsage (usage) {
      if (!Array.isArray(usage)) return []
      return usage.map((item) => {
        const row = item || {}
        return {
          promptKey: row.promptKey || row.prompt_key || row.key || '',
          version: row.version || row.promptVersion || row.prompt_version || '',
          provider: row.provider || '',
          model: row.model || '',
          mode: row.mode || (this.boolFrom(row.webSearchEnabled, row.web_search_enabled, false) ? 'web_search' : 'standard'),
          webSearchEnabled: this.boolFrom(row.webSearchEnabled, row.web_search_enabled, false),
          calls: Number(row.calls || row.count || 0),
          success: Number(row.success || row.successes || 0),
          failed: Number(row.failed || row.failures || 0),
          firstUsedAt: row.firstUsedAt || row.first_used_at || '',
          lastUsedAt: row.lastUsedAt || row.last_used_at || ''
        }
      }).filter((row) => row.promptKey)
    },
    jobOptions (job) {
      const opts = job && Array.isArray(job.options) ? job.options.slice() : []
      if (job && job.key === 'research-part-release-dates') {
        this.ensureOption(opts, { key: 'type', label: 'Part type', type: 'select', choices: ['all', 'cpu', 'gpu', 'motherboard', 'ram', 'storage', 'case', 'psu'], default: 'all' })
        this.ensureOption(opts, { key: 'limit', label: 'Max parts this run', type: 'number', default: 0 })
        this.ensureOption(opts, { key: 'missingOnly', label: 'Missing only', type: 'boolean', default: true })
        this.ensureOption(opts, { key: 'overwrite', label: 'Overwrite existing', type: 'boolean', default: false })
        this.ensureOption(opts, { key: 'delayMs', label: 'Delay between AI calls (ms)', type: 'number', default: 1500 })
      }
      if (job && job.key === 'dedupe-catalog-products') {
        this.ensureOption(opts, { key: 'type', label: 'Part type', type: 'select', choices: ['all', 'cpu', 'gpu', 'motherboard', 'ram', 'storage', 'case', 'psu'], default: 'all' })
        this.ensureOption(opts, { key: 'mode', label: 'Mode', type: 'select', choices: ['report', 'archive', 'delete'], default: 'report' })
        this.ensureOption(opts, { key: 'limit', label: 'Max duplicate groups', type: 'number', default: 0 })
      }
      // Onboard New Parts takes an optional free-text AI research note. Surface it
      // even if the backend doesn't list it as an option, so it ships in the payload.
      if (job && job.key === this.primaryJobKey) {
        this.ensureOption(opts, { key: 'amazonSearch', label: 'Amazon search', type: 'boolean', default: true })
        this.ensureOption(opts, { key: 'scrape', label: 'Scrape', type: 'boolean', default: true })
        this.ensureOption(opts, { key: 'researchMissingSpecs', label: 'Research missing specs', type: 'boolean', default: true })
        this.ensureOption(opts, { key: 'validate', label: 'Validate', type: 'boolean', default: true })
        if (!opts.some((o) => o.key === 'researchNote')) {
          const note = { key: 'researchNote', label: 'AI research note', type: 'textarea', default: '' }
          const qi = opts.findIndex((o) => o.key === 'quantity')
          if (qi > -1) opts.splice(qi + 1, 0, note)
          else opts.push(note)
        }
      }
      return opts
    },
    ensureOption (opts, opt) {
      if (!opts.some((item) => item.key === opt.key)) opts.push(opt)
    },
    boolFrom () {
      for (let i = 0; i < arguments.length; i += 1) {
        const value = arguments[i]
        if (value === undefined || value === null) continue
        if (typeof value === 'string') {
          const normalized = value.toLowerCase()
          if (normalized === 'true' || normalized === '1' || normalized === 'yes') return true
          if (normalized === 'false' || normalized === '0' || normalized === 'no') return false
        }
        return !!value
      }
      return false
    },
    scalarText (value, fallback) {
      if (value === undefined || value === null || value === '') return fallback || ''
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value)
      if (typeof value === 'object') {
        return this.scalarText(value.key || value.promptKey || value.prompt_key || value.name || value.label || value.id, fallback)
      }
      return fallback || ''
    },
    aiPromptBadges (source) {
      if (!source) return []
      return this.normalizeAiPrompts(source.aiPrompts || source.ai_prompts || (source.ai && source.ai.prompts))
    },
    aiPromptUsage (source) {
      if (!source) return []
      return this.normalizeAiPromptUsage(source.aiPromptUsage || source.ai_prompt_usage || (source.ai && source.ai.usage))
    },
    aiPromptMeta (prompt) {
      const provider = prompt.provider || 'default'
      const model = prompt.model || 'default'
      const mode = prompt.mode || (prompt.webSearchEnabled ? 'web_search' : 'standard')
      const version = prompt.version ? `v${prompt.version}` : ''
      return [provider, model, mode, version].filter(Boolean).join(' / ')
    },
    aiPromptTitle (prompt) {
      const parts = [
        prompt.key,
        `Provider: ${prompt.provider || 'default'}`,
        `Model: ${prompt.model || 'default'}`,
        `Mode: ${prompt.mode || (prompt.webSearchEnabled ? 'web_search' : 'standard')}`
      ]
      if (prompt.version) parts.push(`Version: ${prompt.version}`)
      if (prompt.requiresSeed) parts.push('Prompt not seeded')
      return parts.join(' - ')
    },
    runAiLabel (run) {
      if (!run) return ''
      const ai = run.ai || {}
      const first = this.aiPromptBadges(run)[0] || {}
      const provider = ai.primaryProvider || ai.primary_provider || run.aiProvider || run.ai_provider || first.provider || ''
      const model = ai.primaryModel || ai.primary_model || run.aiModel || run.ai_model || first.model || ''
      const mode = ai.primaryMode || ai.primary_mode || first.mode || ''
      if (!provider && !model) return ''
      return ['AI', provider, model, mode].filter(Boolean).join(' / ')
    },
    aiStatValue (source, names) {
      const stats = this.runStats(source)
      const flat = this.flattenStats(stats || {})
      const wanted = names.map((name) => String(name).toLowerCase())
      for (const item of flat) {
        const key = String(item.key || '').toLowerCase()
        const leaf = key.split('.').pop()
        if (wanted.indexOf(key) >= 0 || wanted.indexOf(leaf) >= 0) {
          if (item.value !== undefined && item.value !== null && item.value !== '') return item.value
        }
      }
      return null
    },
    aiOutcome (source) {
      const result = this.aiStatValue(source, ['aiResult', 'ai_result'])
      const status = this.aiStatValue(source, ['aiStatus', 'ai_status'])
      const items = this.aiStatValue(source, ['aiItems', 'ai_items'])
      const webSearchCalls = this.aiStatValue(source, ['aiWebSearchCalls', 'ai_web_search_calls'])
      const preview = this.aiStatValue(source, ['aiOutputPreview', 'ai_output_preview'])
      const blocksRaw = this.aiStatValue(source, ['aiOutputBlocks', 'ai_output_blocks'])
      const blocks = Array.isArray(blocksRaw)
        ? blocksRaw.map((block) => (typeof block === 'string' ? block : JSON.stringify(block, null, 2))).filter(Boolean)
        : []
      const resultText = String(result || '').trim()
      const statusText = String(status || '').trim()
      const visibleResult = resultText || statusText
      let tone = 'neutral'
      if (/^items:/i.test(resultText) || Number(items) > 0) tone = 'good'
      else if (/empty/i.test(resultText)) tone = 'warn'
      else if (/error|fail|quota/i.test(resultText) || /error|fail|quota/i.test(statusText)) tone = 'bad'
      return {
        has: !!(visibleResult || preview || blocks.length || items !== null || webSearchCalls !== null),
        tone,
        resultLabel: visibleResult || 'AI returned telemetry',
        status: statusText && statusText !== resultText ? statusText : '',
        items: items !== null ? this.formatStatValue(items, 'aiitems') : null,
        webSearchCalls: webSearchCalls !== null ? this.formatStatValue(webSearchCalls, 'aiwebsearchcalls') : null,
        preview: preview ? String(preview) : '',
        blocks
      }
    },
    isNoteOpt (opt) {
      return !!opt && (opt.type === 'textarea' || opt.key === 'researchNote')
    },
    researchPlaceholder (job) {
      const vals = this.optionValues[job.key] || {}
      const type = String(vals.type || '').toLowerCase()
      const map = {
        motherboard: 'Find AM5 motherboards with DDR5 support and WiFi.',
        ram: 'Find DDR5 16 GB single-stick kits, 5600 MT/s or better.',
        cpu: 'Find Ryzen 7000 CPUs with AM5 socket.',
        storage: 'Find 2 TB NVMe M.2 SSDs.',
        gpu: 'Find RTX 4070-class GPUs under 300 mm length.'
      }
      return map[type] || 'Optional: describe what to research for this run.'
    },
    async toggleEnabled (job, ev) {
      job.enabled = ev.target.checked
      if (job.enabled && !job.cron) {
        this.applyScheduleDraft(job)
      }
      try {
        await axios.post(`${apiDomain}jobs/${job.key}`,
          { enabled: job.enabled }, { headers: this.authHeaders() })
        if (job.enabled) {
          await this.saveSchedule(job)
          // Turning on Automatic means "run with what I see" — capture the
          // visible run settings as the config the scheduled run will use.
          if (this.configDirty(job)) await this.saveConfig(job)
        }
        this.error = null
      } catch (e) { this.error = this.errMsg(e) }
    },
    async saveSchedule (job) {
      try {
        await axios.post(`${apiDomain}jobs/${job.key}`,
          { cron: job.cron || null }, { headers: this.authHeaders() })
        this.updateScheduleSource(job)
        this.error = null
      } catch (e) { this.error = this.errMsg(e) }
    },
    async saveScheduleFromDraft (job) {
      if (!job.enabled) return
      this.applyScheduleDraft(job)
      await this.saveSchedule(job)
    },
    applyScheduleDraft (job) {
      const draft = this.scheduleDrafts[job.key] || this.scheduleFromCron(job.cron)
      const max = this.scheduleMax(draft.unit)
      const every = Math.max(1, Math.min(max, parseInt(draft.every, 10) || 1))
      draft.every = every
      draft.custom = false
      job.cron = this.cronFromSchedule(draft)
      this.updateScheduleSource(job)
    },
    updateScheduleSource (job) {
      if (this.scheduleDrafts[job.key]) {
        this.scheduleDrafts[job.key].sourceCron = job.cron || ''
      }
    },
    scheduleFromCron (cron) {
      const value = cron ? cron.trim() : ''
      if (!value) return { every: 1, unit: 'days', custom: false, sourceCron: '' }
      if (value === '0 * * * *') return { every: 1, unit: 'hours', custom: false, sourceCron: value }
      if (value === '0 3 * * *') return { every: 1, unit: 'days', custom: false, sourceCron: value }
      if (value === '0 3 * * 1' || value === '0 4 * * 1') {
        return { every: 1, unit: 'weeks', custom: false, sourceCron: value }
      }

      const hours = value.match(/^0 \*\/([0-9]+) \* \* \*$/)
      if (hours) return { every: parseInt(hours[1], 10), unit: 'hours', custom: false, sourceCron: value }

      const days = value.match(/^0 3 \*\/([0-9]+) \* \*$/)
      if (days) return { every: parseInt(days[1], 10), unit: 'days', custom: false, sourceCron: value }

      return { every: 1, unit: 'days', custom: true, sourceCron: value }
    },
    cronFromSchedule (draft) {
      const every = Math.max(1, parseInt(draft.every, 10) || 1)
      if (draft.unit === 'hours') {
        return every === 1 ? '0 * * * *' : `0 */${every} * * *`
      }
      if (draft.unit === 'weeks') {
        return '0 3 * * 1'
      }
      return every === 1 ? '0 3 * * *' : `0 3 */${every} * *`
    },
    scheduleMax (unit) {
      if (unit === 'hours') return 23
      if (unit === 'weeks') return 1
      return 31
    },
    scheduleHint (job) {
      if (!job.enabled) return 'Runs only when you click Run Now.'
      const draft = this.scheduleDrafts[job.key]
      if (draft && draft.custom) return `Custom cron: ${job.cron}`
      if (!draft) return this.cronHint(job.cron)
      const unit = this.scheduleUnitLabel(draft)
      return `Runs every ${draft.every} ${unit}.`
    },
    scheduleUnitLabel (draft) {
      if (!draft) return 'days'
      if (draft.unit === 'weeks') return 'week'
      if (draft.every === 1 && draft.unit === 'hours') return 'hour'
      if (draft.every === 1 && draft.unit === 'days') return 'day'
      return draft.unit
    },
    async viewRun (run) {
      // Subscribe before the detail request so no live log line can fall into
      // the HTTP-to-WebSocket handoff gap.
      this.selectedRun = this.normalizeRun(Object.assign({}, run || {}))
      this.subscribeSelectedRun()
      try {
        const { data } = await axios.get(`${apiDomain}jobs/runs/${run.id}`,
          { headers: this.authHeaders() })
        this.selectedRun = this.mergeSelectedRunSnapshot(data.run)
      } catch (e) { this.error = this.errMsg(e) }
    },
    async cancelRun (run) {
      try {
        await axios.post(`${apiDomain}jobs/runs/${run.id}/cancel`, {},
          { headers: this.authHeaders() })
        this.fetchAll()
      } catch (e) { this.error = this.errMsg(e) }
    },
    isActive (job) {
      return !!this.activeRunForJob(job)
    },
    jobName (key) {
      const j = this.jobs.find((x) => x.key === key)
      if (j) return this.scalarText(j.name || j.label, this.prettyLabel(key))
      const run = (this.runs || []).find((item) => item.jobKey === key && item.jobName)
      return this.scalarText(run && run.jobName, this.prettyLabel(key) || String(key || 'Unknown job'))
    },
    statusLabel (status) {
      if (!status) return 'Never run'
      return this.prettyLabel(status)
    },
    executionStatusLabel (status) {
      const value = String(status || '').toLowerCase()
      if (!value || value === 'none') return 'Never run'
      if (value === 'success' || value === 'succeeded' || value === 'complete' || value === 'completed') return 'Completed'
      return this.statusLabel(status)
    },
    prettyLabel (value) {
      return String(value || '')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    },
    statsSummary (stats) {
      if (!stats) return ''
      return this.flattenStats(stats)
        .map(({ key, value }) => `${key}: ${this.formatStatValue(value, this.statLeaf(key).toLowerCase())}`)
        .join(' - ')
    },
    flattenStats (stats, prefix) {
      // Flatten nested stats (e.g. discover.stored, scrape.enriched, research.fieldsFilled)
      // into dotted keys so the onboarding pipeline summary reads cleanly.
      const out = []
      Object.keys(stats || {}).forEach((k) => {
        const value = stats[k]
        const key = prefix ? `${prefix}.${k}` : k
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          out.push(...this.flattenStats(value, key))
        } else if (Array.isArray(value)) {
          out.push({ key, value })
        } else if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
          out.push({ key, value })
        }
      })
      return out
    },
    statLeaf (key) {
      const k = String(key)
      const i = k.lastIndexOf('.')
      return i > -1 ? k.slice(i + 1) : k
    },
    spaceKey (key) {
      return String(key).replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    },
    isConfigStat (key, value) {
      // Config/inputs are the request, not the result. Keep them out of the
      // metric funnel: top-level booleans plus known option keys.
      if (key.indexOf('.') > -1) return false
      if (typeof value === 'boolean') return true
      return /^(type|quantity|delay|delayms|limit|mode|source|category|status|note|researchnote|provider|model|websearchenabled|missingonly|overwrite|aiprompts|aipromptusage)$/i.test(key)
    },
    statTone (key, value) {
      const k = this.statLeaf(key).toLowerCase()
      const num = typeof value === 'number' ? value : null
      const positiveCount = num === null || num > 0
      // Failures / invalid data — only alarming when there is actually a count.
      if (/(fail|error|invalid|dead|blocked)/.test(k)) {
        return num === 0 ? 'neutral' : 'bad'
      }
      // Things worth a glance: duplicates, skips, and unresolved leftovers.
      // (Plain "missing"/workload counts stay neutral — only leftovers warn.)
      if (/(duplicate|skip|stale|still|remaining|unresolved|backlog|leftmissing|needsreview)/.test(k)) {
        return num === 0 ? 'neutral' : 'warn'
      }
      // Useful work produced.
      if (/(stored|enriched|filled|alive|imported|updated|fixed|healed|created)/.test(k)) {
        return positiveCount ? 'good' : 'neutral'
      }
      return 'neutral'
    },
    groupDenominator (items) {
      // The funnel "starting count" a stage's outcomes are measured against.
      const priority = ['requested', 'queued', 'total', 'candidates', 'processed', 'found']
      for (const base of priority) {
        const hit = items.find((s) => this.statLeaf(s.key).toLowerCase() === base && typeof s.value === 'number')
        if (hit && hit.value > 0) return { value: hit.value, leaf: base }
      }
      return null
    },
    makeChip (stat, denom, isParam) {
      const leaf = this.statLeaf(stat.key)
      const lc = leaf.toLowerCase()
      // Ratios only make sense for part-level counts, not field tallies.
      const isOutcome = /(stored|enriched|found|alive|imported|updated|fixed|healed|created|processed)/.test(lc) &&
        !lc.includes('field')
      const showDenom = !isParam && denom && isOutcome &&
        typeof stat.value === 'number' && lc !== denom.leaf
      return {
        key: stat.key,
        label: this.prettyLabel(this.spaceKey(leaf)),
        value: this.formatStatValue(stat.value, lc),
        denom: showDenom ? denom.value : null,
        tone: isParam ? 'param' : this.statTone(stat.key, stat.value),
        isZero: typeof stat.value === 'number' && stat.value === 0,
        isParam: !!isParam
      }
    },
    formatStatValue (value, key) {
      if (typeof value === 'boolean') return value ? 'yes' : 'no'
      if (Array.isArray(value)) {
        if (!value.length) return '0'
        if (/candidateids|ids$/.test(key || '')) return `${value.length} ids`
        if (/aiprompts?/.test(key || '')) return `${value.length} prompt${value.length === 1 ? '' : 's'}`
        if (/aipromptusage/.test(key || '')) return `${value.length} usage row${value.length === 1 ? '' : 's'}`
        if (value.some((item) => item && typeof item === 'object')) return `${value.length} items`
        return value.length > 4 ? `${value.length} items` : value.join(', ')
      }
      return value
    },
    stageRank (stage) {
      const key = String(stage || '').toLowerCase()
      const order = {
        summary: 0,
        discover: 10,
        discovery: 10,
        amazonsearch: 20,
        amazon_search: 20,
        scrape: 30,
        scraping: 30,
        research: 40,
        enrich: 40,
        validate: 50,
        verification: 50,
        verify: 50,
        store: 60,
        import: 60
      }
      return order[key] || 100
    },
    isPipelineStageGroup (group) {
      const key = String(group && group.rawStage || '').toLowerCase()
      return [
        'discover', 'discovery', 'amazonsearch', 'amazon_search',
        'scrape', 'scraping', 'research', 'enrich', 'validate',
        'verification', 'verify', 'store', 'import'
      ].indexOf(key) >= 0
    },
    resultModel (stats, run) {
      const flat = this.flattenStats(stats)
      const params = []
      const metrics = []
      let note = ''
      flat.forEach((s) => {
        const leaf = this.statLeaf(s.key).toLowerCase()
        if (leaf === 'researchnote' || leaf === 'note') {
          if (s.value) note = String(s.value)
          return
        }
        if (this.isConfigStat(s.key, s.value)) params.push(this.makeChip(s, null, true))
        else metrics.push(s)
      })

      // Group metrics by pipeline stage (prefix before the first dot).
      const groupMap = {}
      const order = []
      metrics.forEach((s) => {
        const dot = s.key.indexOf('.')
        const stage = dot > -1 ? s.key.slice(0, dot) : ''
        if (!groupMap[stage]) { groupMap[stage] = []; order.push(stage) }
        groupMap[stage].push(s)
      })
      const groups = order.map((stage) => {
        const items = groupMap[stage]
        const denom = this.groupDenominator(items)
        return {
          key: stage || 'summary',
          stage: stage ? this.prettyLabel(this.spaceKey(stage)) : '',
          rawStage: stage,
          stats: items,
          chips: items.map((s) => this.makeChip(s, denom, false))
        }
      }).sort((a, b) => this.stageRank(a.rawStage) - this.stageRank(b.rawStage))

      // Tag every chip with its stage so the headline can disambiguate stages.
      groups.forEach((g) => { g.chips.forEach((c) => { c.stage = g.stage }) })

      const stages = groups
        .filter((g) => g.stage && this.isPipelineStageGroup(g))
        .map((group) => this.stageModel(group, run))
      const looseGroups = groups.filter((g) => !g.stage || !this.isPipelineStageGroup(g))

      // Headline: the first positive outcome of each stage, plus any live problems.
      const goods = []
      const bad = []
      const warn = []
      groups.forEach((g) => {
        const firstGood = g.chips.find((c) => c.tone === 'good' && !c.isZero)
        if (firstGood) goods.push(firstGood)
        g.chips.forEach((c) => {
          if (c.isZero) return
          if (c.tone === 'bad') bad.push(c)
          else if (c.tone === 'warn') warn.push(c)
        })
      })
      const problems = bad.concat(warn)
      const allChips = groups.reduce((acc, g) => acc.concat(g.chips), [])
      let summary = goods.concat(problems)
      if (summary.length > 5) {
        const keepGoods = Math.max(0, 5 - problems.length)
        summary = goods.slice(0, keepGoods).concat(problems).slice(0, 5)
      }
      if (!summary.length) summary = allChips.slice(0, 2)

      const namedGroups = stages.length
      const detail = allChips.length > summary.length || params.length > 0 || namedGroups > 0 || !!note
      return {
        params,
        groups,
        looseGroups,
        stages,
        summary,
        detail,
        multiStage: namedGroups > 1,
        note,
        headline: this.pipelineHeadline(stages, summary, run),
        stageLine: this.pipelineStageLine(stages),
        currentTone: this.pipelineTone(stages, run)
      }
    },
    stageModel (group, run) {
      const stats = group.stats || []
      const chips = group.chips || []
      const key = String(group.rawStage || group.key || '').toLowerCase()
      const denom = this.groupDenominator(stats)
      const progress = this.stageProgress(key, stats, denom)
      const status = this.stageStatus(key, stats, chips, progress, run)
      return {
        key: group.rawStage || group.key,
        label: group.stage || this.prettyLabel(key),
        status,
        statusLabel: this.prettyLabel(status),
        summary: this.stageSummary(key, stats, chips, progress),
        promptKey: this.stagePromptKey(key, stats, run),
        chips
      }
    },
    stageProgress (stageKey, stats, denom) {
      const wantedByStage = {
        discover: ['stored', 'found', 'requested'],
        discovery: ['stored', 'found', 'requested'],
        amazonsearch: ['stored', 'found', 'requested'],
        amazon_search: ['stored', 'found', 'requested'],
        scrape: ['processed', 'enriched', 'queued'],
        scraping: ['processed', 'enriched', 'queued'],
        research: ['processed', 'enriched', 'fieldsfilled', 'queued'],
        validate: ['processed', 'passed', 'valid', 'checked'],
        verification: ['processed', 'passed', 'valid', 'checked'],
        verify: ['processed', 'passed', 'valid', 'checked'],
        store: ['stored', 'imported', 'created', 'updated'],
        import: ['stored', 'imported', 'created', 'updated']
      }
      const wanted = wantedByStage[stageKey] || ['processed', 'stored', 'enriched', 'found', 'updated']
      const normalized = stats.map((stat) => ({
        stat,
        leaf: this.statLeaf(stat.key).toLowerCase()
      }))
      for (const leaf of wanted) {
        const hit = normalized.find((entry) => entry.leaf === leaf && typeof entry.stat.value === 'number')
        if (hit) {
          return {
            value: hit.stat.value,
            total: denom ? denom.value : null,
            label: this.prettyLabel(this.spaceKey(hit.leaf))
          }
        }
      }
      return {
        value: null,
        total: denom ? denom.value : null,
        label: ''
      }
    },
    stageStatus (stageKey, stats, chips, progress, run) {
      const explicit = this.stageExplicitStatus(stats)
      if (explicit) return explicit
      const hasBad = chips.some((chip) => chip.tone === 'bad' && !chip.isZero)
      if (hasBad) return 'failed'

      const runStatus = run && run.status ? String(run.status).toLowerCase() : ''
      const runningRun = runStatus === 'running' || runStatus === 'queued'
      if (progress && progress.total && progress.value !== null) {
        if (progress.value >= progress.total) return 'completed'
        if (runningRun && progress.value > 0) return 'running'
        if (progress.value > 0) return 'partial'
      }
      if (runningRun && this.stageHasWork(stats)) return 'running'
      if (this.stageHasWork(stats)) return 'completed'
      return runStatus === 'success' ? 'completed' : 'waiting'
    },
    stageExplicitStatus (stats) {
      const status = stats.find((stat) => {
        const leaf = this.statLeaf(stat.key).toLowerCase()
        return leaf === 'status' || leaf === 'aistatus' || leaf === 'state'
      })
      if (!status || !status.value) return ''
      const value = String(status.value).toLowerCase()
      if (/complete|success|done|ok/.test(value)) return 'completed'
      if (/fail|error|blocked/.test(value)) return 'failed'
      if (/run|process|progress|active/.test(value)) return 'running'
      if (/queue|wait|pending/.test(value)) return 'waiting'
      if (/skip|cancel/.test(value)) return value.indexOf('cancel') > -1 ? 'cancelled' : 'skipped'
      return ''
    },
    stageHasWork (stats) {
      return stats.some((stat) => {
        if (typeof stat.value === 'number') return stat.value > 0
        return !!stat.value
      })
    },
    stageSummary (stageKey, stats, chips, progress) {
      const get = (names) => {
        for (const name of names) {
          const stat = stats.find((s) => this.statLeaf(s.key).toLowerCase() === name)
          if (stat && stat.value !== undefined && stat.value !== null && stat.value !== '') return stat.value
        }
        return null
      }
      const found = get(['found'])
      const stored = get(['stored', 'imported', 'created'])
      const requested = get(['requested'])
      const queued = get(['queued'])
      const processed = get(['processed'])
      const enriched = get(['enriched'])
      const notFound = get(['notfound'])
      const fieldsMissing = get(['fieldsmissing', 'withmissingfields'])
      const fieldsFilled = get(['fieldsfilled'])
      const duplicates = get(['duplicates'])
      const invalidAsin = get(['invalidasin'])
      const invalidUrl = get(['invalidurl'])
      const candidateIds = get(['candidateids'])

      if (stageKey === 'discover' || stageKey === 'discovery') {
        const parts = []
        if (found !== null) parts.push(`${found} found`)
        if (stored !== null) parts.push(`${stored} stored`)
        if (!parts.length && requested !== null) parts.push(`${requested} requested`)
        return parts.join(' / ') || this.stageFallbackSummary(chips, progress)
      }
      if (stageKey === 'amazonsearch' || stageKey === 'amazon_search') {
        const parts = []
        if (found !== null) parts.push(`${found} found`)
        if (stored !== null) parts.push(`${stored} stored`)
        if (!parts.length && requested !== null) parts.push(`${requested} requested`)
        if (duplicates) parts.push(`${duplicates} duplicates`)
        if (invalidAsin) parts.push(`${invalidAsin} invalid ASIN`)
        if (invalidUrl) parts.push(`${invalidUrl} invalid URL`)
        if (Array.isArray(candidateIds) && candidateIds.length) parts.push(`${candidateIds.length} candidates`)
        return parts.join(' / ') || this.stageFallbackSummary(chips, progress)
      }
      if (stageKey === 'scrape' || stageKey === 'scraping') {
        const parts = []
        if (processed !== null && queued !== null) parts.push(`${processed}/${queued} processed`)
        else if (processed !== null) parts.push(`${processed} processed`)
        if (enriched !== null) parts.push(`${enriched} enriched`)
        else if (notFound !== null) parts.push(`${notFound} not found`)
        return parts.join(' / ') || this.stageFallbackSummary(chips, progress)
      }
      if (stageKey === 'research') {
        const parts = []
        if (processed !== null && queued !== null) parts.push(`${processed}/${queued} researched`)
        else if (processed !== null) parts.push(`${processed} researched`)
        if (fieldsFilled !== null) parts.push(`${fieldsFilled} fields filled`)
        if (fieldsMissing !== null) parts.push(`${fieldsMissing} missing`)
        if (enriched !== null && fieldsFilled === null) parts.push(`${enriched} enriched`)
        return parts.join(' / ') || this.stageFallbackSummary(chips, progress)
      }
      return this.stageFallbackSummary(chips, progress)
    },
    stageFallbackSummary (chips, progress) {
      if (progress && progress.value !== null) {
        return progress.total ? `${progress.value}/${progress.total} ${progress.label}` : `${progress.value} ${progress.label}`
      }
      const primary = chips.find((chip) => !chip.isZero && chip.tone !== 'param') || chips[0]
      if (!primary) return 'Waiting for data'
      return `${primary.value}${primary.denom ? '/' + primary.denom : ''} ${primary.label.toLowerCase()}`
    },
    stagePromptKey (stageKey, stats, run) {
      const explicit = stats.find((stat) => {
        const leaf = this.statLeaf(stat.key).toLowerCase()
        return leaf === 'prompt' || leaf === 'promptkey' || leaf === 'prompt_key'
      })
      if (explicit && explicit.value) return String(explicit.value)

      const jobKey = run && run.jobKey ? run.jobKey : ''
      const onboard = {
        discover: 'candidate-discovery',
        discovery: 'candidate-discovery',
        amazonsearch: 'amazon-product-search',
        amazon_search: 'amazon-product-search',
        scrape: 'candidate-spec-extract',
        scraping: 'candidate-spec-extract',
        research: 'candidate-spec-research',
        validate: 'spec-verify',
        verification: 'spec-verify',
        verify: 'spec-verify'
      }
      const jobPrompts = {
        'discover-new-parts': 'candidate-discovery',
        'amazon-product-search': 'amazon-product-search',
        'scrape-candidate-parts': 'candidate-spec-extract',
        'enrich-candidate-specs': 'candidate-spec-research',
        'research-part-release-dates': 'part-release-date-research',
        'catalog-health-ai-research': 'catalog-health-research'
      }
      if (jobKey === this.primaryJobKey && onboard[stageKey]) return onboard[stageKey]
      return jobPrompts[jobKey] || ''
    },
    pipelineHeadline (stages, summary, run) {
      if (!stages.length) {
        const chip = summary && summary.length ? summary[0] : null
        return chip ? `${chip.label} ${chip.value}${chip.denom ? '/' + chip.denom : ''}` : ''
      }
      const active = stages.find((stage) => stage.status === 'running') ||
        stages.find((stage) => stage.status === 'failed') ||
        stages.find((stage) => stage.status === 'partial')
      if (active) return `${active.statusLabel}: ${active.label}`
      const runStatus = run && run.status ? String(run.status).toLowerCase() : ''
      if (runStatus === 'success') return 'Pipeline completed'
      return stages[stages.length - 1].summary
    },
    pipelineStageLine (stages) {
      if (!stages.length) return ''
      return stages.map((stage) => `${stage.label} ${stage.statusLabel.toLowerCase()}`).join(' - ')
    },
    pipelineTone (stages, run) {
      if (stages.some((stage) => stage.status === 'failed')) return 'bad'
      if (stages.some((stage) => stage.status === 'running')) return 'live'
      if (stages.some((stage) => stage.status === 'partial')) return 'warn'
      const runStatus = run && run.status ? String(run.status).toLowerCase() : ''
      if (runStatus === 'failed') return 'bad'
      if (runStatus === 'running' || runStatus === 'queued') return 'live'
      return 'good'
    },
    isOnboardingScopeJob (job) {
      return !!(job && ONBOARDING_JOB_KEYS.indexOf(job.key) !== -1)
    },
    isAutoOnboardJob (job) {
      return !!(job && job.key === 'catalog-auto-onboard')
    },
    scopePlanActionable (scope) {
      return !!(scope && scope.plan && scope.plan.actionable === true)
    },
    scopeReadinessBlocked (scope, job) {
      if (this.isOnboardingScopeJob(job) && scope && scope.plan) {
        return !this.scopePlanActionable(scope) || scope.canRun === false
      }
      return !!(scope && scope.canRun === false)
    },
    scopeReadinessLabel (scope, job) {
      if (this.isOnboardingScopeJob(job) && scope && scope.plan) {
        if (!this.scopePlanActionable(scope)) return 'No gaps to onboard'
        return this.isAutoOnboardJob(job) ? 'Ready to onboard' : 'Onboarding gaps found'
      }
      return scope && scope.canRun === false ? 'No eligible work' : 'Ready to run'
    },
    scopeRunWarning (scope, job) {
      if (this.isAutoOnboardJob(job) && scope && scope.plan && !this.scopePlanActionable(scope)) {
        return this.manualReviewDirectiveItems(scope).length
          ? 'No catalog gaps need onboarding. Retirement suggestions below are report-only and require manual review.'
          : 'No catalog gaps need onboarding with the current options.'
      }
      return scope && scope.canRun === false ? 'No eligible work for the current options.' : ''
    },
    scopeDescriptorTitle (scope) {
      const descriptor = scope && scope.descriptor ? scope.descriptor : {}
      return descriptor.title || descriptor.name || this.prettyLabel(descriptor.kind || descriptor.entity || 'Eligible work')
    },
    scopeDescriptorDescription (scope) {
      const descriptor = scope && scope.descriptor ? scope.descriptor : {}
      return descriptor.description || descriptor.summary || descriptor.entity || 'Preview the work this job will attempt with the current options.'
    },
    scopeCountGroups (scope, job) {
      if (!scope) return []
      const groups = []
      const hasOnboardingPlan = this.isOnboardingScopeJob(job) && !!scope.plan
      const add = (key, label, value) => {
        const items = this.scopeObjectItems(value)
        if (items.length) groups.push({ key, label, items })
      }
      if (hasOnboardingPlan) {
        const onboard = scope.plan.onboard !== undefined
          ? scope.plan.onboard
          : this.onboardDirectiveItems(scope).length
        const retire = scope.plan.retire !== undefined
          ? scope.plan.retire
          : this.manualReviewDirectiveItems(scope).length
        groups.push({
          key: 'plan',
          label: 'Plan',
          items: [
            { key: 'onboard', label: 'Onboard', value: this.formatScopeValue(onboard) },
            { key: 'retire', label: 'Retire (manual)', value: this.formatScopeValue(retire) }
          ]
        })
      } else {
        add('total', 'Summary', {
          total: scope.total,
          eligible: scope.eligible,
          selected: scope.selected,
          limit: scope.limit
        })
      }
      if (!hasOnboardingPlan) add('counts', 'Counts', scope.counts)
      add('byType', 'By type', scope.byType || scope.by_type)
      add('byStatus', 'By status', scope.byStatus || scope.by_status)
      return groups.filter((group) => group.items.length)
    },
    scopeObjectItems (value) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return []
      return Object.keys(value).map((key) => {
        const item = value[key]
        if (item === undefined || item === null || item === '') return null
        if (typeof item === 'object' && !Array.isArray(item)) return null
        return {
          key,
          label: this.prettyLabel(key),
          value: this.formatScopeValue(item)
        }
      }).filter(Boolean)
    },
    formatScopeValue (value) {
      if (typeof value === 'boolean') return value ? 'yes' : 'no'
      if (Array.isArray(value)) return value.length ? `${value.length} items` : '0'
      return value
    },
    runStats (source) {
      if (!source) return {}
      return source.stats && typeof source.stats === 'object' ? source.stats : source
    },
    statValue (stats, keys) {
      const source = stats || {}
      for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null && source[key] !== '') return source[key]
      }
      const lowerMap = {}
      Object.keys(source).forEach((key) => { lowerMap[key.toLowerCase()] = source[key] })
      for (const key of keys) {
        const value = lowerMap[String(key).toLowerCase()]
        if (value !== undefined && value !== null && value !== '') return value
      }
      return null
    },
    dedupeReportRows (source) {
      const stats = this.runStats(source)
      const nested = stats.dedupe && typeof stats.dedupe === 'object' ? stats.dedupe : {}
      const report = stats.report || stats.duplicateReport || stats.duplicate_report || nested.report || []
      if (!Array.isArray(report)) return []
      return report.map((row, index) => {
        const item = row || {}
        const drop = Array.isArray(item.drop) ? item.drop : (Array.isArray(item.dropped) ? item.dropped : item.dropIds)
        const dropped = Array.isArray(drop)
          ? drop.join(', ')
          : (drop !== undefined && drop !== null && drop !== '' ? String(drop) : '-')
        const type = item.type || item.part_type || '-'
        const asin = item.asin || '-'
        const keep = item.keep || item.keepId || item.keep_id || '-'
        return {
          key: [type, asin, keep, index].join(':'),
          type,
          asin,
          keep,
          drop: dropped
        }
      })
    },
    dedupeSummaryRows (source) {
      const stats = this.runStats(source)
      const nested = stats.dedupe && typeof stats.dedupe === 'object' ? stats.dedupe : {}
      const merged = Object.assign({}, nested, stats)
      const defs = [
        { key: 'mode', label: 'Mode', keys: ['mode'] },
        { key: 'duplicateGroups', label: 'Duplicate groups', keys: ['duplicateGroups', 'duplicate_groups'] },
        { key: 'duplicateParts', label: 'Duplicate parts', keys: ['duplicateParts', 'duplicate_parts'] },
        { key: 'kept', label: 'Kept', keys: ['kept', 'keep'] },
        { key: 'archived', label: 'Archived', keys: ['archived'] },
        { key: 'deleted', label: 'Deleted', keys: ['deleted'] },
        { key: 'skipped', label: 'Skipped', keys: ['skipped'] }
      ]
      return defs.map((def) => {
        const value = this.statValue(merged, def.keys)
        if (value === null) return null
        return {
          key: def.key,
          label: def.label,
          value: this.formatScopeValue(value)
        }
      }).filter(Boolean)
    },
    scopeWorkflowStages (scope) {
      const workflow = scope && scope.workflow ? scope.workflow : null
      const stages = workflow && Array.isArray(workflow.stages) ? workflow.stages : (Array.isArray(scope && scope.stages) ? scope.stages : [])
      return stages || []
    },
    scopeDetailGroups (scope) {
      if (!scope) return []
      const keys = ['source', 'report', 'aggregation']
      return keys.map((key) => {
        const items = this.scopeObjectItems(scope[key])
        return {
          key,
          label: this.prettyLabel(key),
          items
        }
      }).filter((group) => group.items.length)
    },
    scopeItems (scope) {
      return scope && Array.isArray(scope.items) ? scope.items : []
    },
    scopeItemKey (item) {
      return [item.entity, item.type || item.part_type, item.id || item.target_id || item.asin || item.model].filter(Boolean).join(':')
    },
    openPrompt (promptKey) {
      if (!promptKey) return
      this.$router.push({
        path: '/users/ai-prompts',
        query: { prompt: promptKey }
      }).catch(() => {})
    },
    openDetail (job) {
      this.detailJob = job
      this.scopePreview = null
      this.scopeError = ''
      this.fetchScopePreview(job)
    },
    lastRunModel (job) {
      if (!job || !job.lastRun) return { summary: [], multiStage: false, headline: '', currentTone: 'neutral' }
      const model = this.resultModel(job.lastRun.stats, job.lastRun)
      return {
        summary: model.summary.slice(0, 4),
        multiStage: model.multiStage,
        headline: model.headline,
        currentTone: model.currentTone
      }
    },
    jobResultLabel (job) {
      const model = this.lastRunModel(job)
      if (model.headline && model.headline !== 'Pipeline completed') return model.headline
      const chips = model.summary.slice(0, 2).map((chip) => {
        return `${chip.label} ${chip.value}${chip.denom ? '/' + chip.denom : ''}`
      })
      return chips.join(' · ') || model.headline || 'Completed'
    },
    isRunning (run) {
      const status = run && run.status ? String(run.status).toLowerCase() : ''
      return status === 'running' || status === 'queued' || status === 'starting'
    },
    isTestRun (run) {
      const stats = run && run.stats
      if (!stats || typeof stats !== 'object') return false
      return stats.testMode === true || stats.test_mode === true ||
        String(stats.testMode || stats.test_mode || '').toLowerCase() === 'true'
    },
    isExpanded (run) {
      if (run.id in this.expandedRuns) return this.expandedRuns[run.id]
      return this.isRunning(run)
    },
    isOnboardingRun (run) {
      const key = run && (run.jobKey || run.job || run.key)
      return ONBOARDING_JOB_KEYS.indexOf(key) !== -1
    },
    isReportOnlyDirective (item) {
      if (!item) return false
      return item.entity === 'retire_directive' || item.action === 'retire' || item.reportOnly === true
    },
    onboardDirectiveItems (scope) {
      return this.scopeItems(scope).filter((item) => {
        if (!item || this.isReportOnlyDirective(item)) return false
        return item.entity === 'onboard_directive' || item.entity === 'coverage_deficit' || item.action === 'onboard'
      })
    },
    manualReviewDirectiveItems (scope) {
      return this.scopeItems(scope).filter((item) => this.isReportOnlyDirective(item))
    },
    hasOnboardingDirectiveItems (scope) {
      return this.onboardDirectiveItems(scope).length > 0 || this.manualReviewDirectiveItems(scope).length > 0
    },
    scopeDirectiveTier (item) {
      const k = String((item && item.tier) || '').toLowerCase()
      return ['platform-keystone', 'subpath-keystone', 'variety'].indexOf(k) !== -1 ? k : 'variety'
    },
    scopeDirectiveTierLabel (item) {
      const map = { 'platform-keystone': 'Platform keystone', 'subpath-keystone': 'Sub-path keystone', variety: 'Variety' }
      return map[this.scopeDirectiveTier(item)]
    },
    scopeDirectiveTitle (item) {
      if (!item) return ''
      const labels = { cpu: 'CPU', motherboard: 'Motherboard', ram: 'RAM', storage: 'Storage', gpu: 'GPU', psu: 'PSU', case: 'Case' }
      const type = String(item.type || '').toLowerCase()
      const typeLabel = labels[type] || (type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Part')
      const verb = item.entity === 'retire_directive' ? 'Retire ' : ''
      const count = item.count != null ? item.count + '× ' : ''
      return `${verb}${count}${typeLabel}`
    },
    scopeDirectiveConstraints (item) {
      const c = (item && item.constraints) || {}
      const chips = []
      if (c.socket) chips.push(`socket ${c.socket}`)
      if (c.ram_type) chips.push(`ram ${String(c.ram_type).toUpperCase()}`)
      if (c.form_factor) chips.push(`form ${c.form_factor}`)
      if (Array.isArray(c.price_bands) && c.price_bands.length) chips.push(`bands ${c.price_bands.join('/')}`)
      if (Array.isArray(item && item.sockets) && item.sockets.length && !c.socket) chips.push(item.sockets.join(', '))
      return chips
    },
    toggleExpand (run) {
      this.$set(this.expandedRuns, run.id, !this.isExpanded(run))
    },
    fmtRelative (t) {
      if (!t) return '--'
      const d = new Date(t)
      if (isNaN(d)) return '--'
      const diff = Date.now() - d.getTime()
      const s = Math.round(diff / 1000)
      if (s < 0) return this.fmtTime(t)
      if (s < 45) return 'just now'
      const m = Math.round(s / 60)
      if (m < 60) return `${m}m ago`
      const h = Math.round(m / 60)
      if (h < 24) return `${h}h ago`
      const days = Math.round(h / 24)
      if (days < 7) return `${days}d ago`
      return this.fmtTime(t)
    },
    fmtTableTime (t) {
      if (!t) return '--'
      const d = new Date(t)
      if (isNaN(d)) return '--'
      const diff = Date.now() - d.getTime()
      if (diff >= 0 && diff < 7 * 24 * 60 * 60 * 1000) return this.fmtRelative(t)
      const options = { month: 'short', day: 'numeric' }
      if (d.getFullYear() !== new Date().getFullYear()) options.year = 'numeric'
      return d.toLocaleDateString(undefined, options)
    },
    cronHint (cron) {
      if (!cron) return 'Manual only'
      const map = {
        '0 3 * * *': 'Daily at 03:00',
        '0 4 * * 1': 'Mondays at 04:00',
        '0 3 * * 1': 'Weekly on Monday',
        '0 * * * *': 'Every hour',
        '*/15 * * * *': 'Every 15 minutes'
      }
      return map[cron.trim()] || cron
    },
    duration (run) {
      if (!run.startedAt) return '--'
      const end = run.finishedAt ? new Date(run.finishedAt) : new Date()
      const ms = end - new Date(run.startedAt)
      if (ms < 0) return '--'
      const s = Math.round(ms / 1000)
      return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`
    },
    fmtTime (t) {
      if (!t) return '--'
      const d = new Date(t)
      return isNaN(d) ? '--' : d.toLocaleString()
    },
    errMsg (e) {
      return (e.response && e.response.data && e.response.data.message) || e.message || 'Request failed'
    }
  }
}
</script>

<style scoped>
.jobs-page {
  padding: 4px 8px 44px;
  color: #cfd2da;
}

.jobs-hero {
  max-width: none;
}

.label {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  border-bottom: 1px solid rgba(67, 72, 87, 0.9);
}

.label h4 {
  margin: 0 0 6px;
  color: #d8dde8;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.eyebrow {
  display: block;
  margin-bottom: 3px;
  color: #1997c6;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.intro {
  max-width: 820px;
  margin: 0.6rem 0 1.1rem;
  color: #a4acbc;
  font-size: 14px;
  line-height: 1.55;
}

.jobs-view-tabs {
  display: inline-flex;
  gap: 4px;
  margin: 4px 0 14px;
  padding: 4px;
  border: 1px solid rgba(67, 72, 87, 0.86);
  border-radius: 8px;
  background: rgba(7, 11, 18, 0.55);
}

.jobs-view-tabs button {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  min-width: 132px;
  padding: 8px 12px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #8f98aa;
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
}

.jobs-view-tabs button:hover {
  color: #d7dde8;
}

.jobs-view-tabs button.active {
  background: rgba(25, 151, 198, 0.14);
  color: #cceefd;
  box-shadow: inset 0 0 0 1px rgba(31, 178, 232, 0.32);
}

.jobs-view-tabs b {
  min-width: 22px;
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(67, 72, 87, 0.5);
  color: #aeb6c8;
  font-size: 10px;
  text-align: center;
}

.jobs-view-tabs button.active b {
  background: rgba(31, 178, 232, 0.2);
  color: #73d8fb;
}

.jobs-table-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px 16px;
  margin-bottom: 10px;
}

.jobs-category-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.jobs-category-filter button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 6px;
  background: rgba(9, 13, 20, 0.48);
  color: #8f98aa;
  cursor: pointer;
  font-size: 11px;
  font-weight: 800;
}

.jobs-category-filter button:hover,
.jobs-category-filter button.active {
  border-color: rgba(25, 151, 198, 0.68);
  color: #d9e9f0;
}

.jobs-category-filter button.active {
  background: rgba(25, 151, 198, 0.12);
}

.jobs-category-filter b {
  color: #6fcff0;
  font-size: 10px;
}

.jobs-category-filter i {
  padding-left: 7px;
  border-left: 1px solid rgba(27, 201, 142, 0.26);
  color: #7ee0bb;
  font-size: 9px;
  font-style: normal;
}

.active-category-description {
  grid-column: 1 / -1;
  margin: 0;
  color: #7f899b;
  font-size: 12px;
  line-height: 1.4;
}

.alert {
  margin: 12px 0;
  padding: 10px 12px;
  border: 1px solid rgba(232, 79, 106, 0.5);
  border-radius: 8px;
  background: rgba(90, 34, 48, 0.48);
  color: #f3c6cf;
}

.notice {
  margin: 12px 0;
  padding: 10px 12px;
  border: 1px solid rgba(27, 201, 142, 0.42);
  border-radius: 8px;
  background: rgba(27, 201, 142, 0.1);
  color: #a6ead2;
  font-size: 12.5px;
}

.job-mutation-warning {
  display: grid;
  gap: 4px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(224, 192, 126, 0.42);
  border-radius: 8px;
  background: rgba(77, 58, 31, 0.15);
  color: #d9c48c;
  font-size: 12px;
  line-height: 1.4;
}

.job-mutation-warning strong {
  color: #f0d38c;
  font-size: 11px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.job-mutation-warning.danger {
  border-color: rgba(232, 79, 106, 0.58);
  background: rgba(90, 34, 48, 0.24);
  color: #f2b3bf;
}

.job-mutation-warning.danger strong {
  color: #ffb1c0;
}

.job-category-tabs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(174px, 1fr));
  gap: 10px;
  margin: 16px 0 12px;
}

.job-category-tab {
  display: grid;
  gap: 5px;
  min-height: 82px;
  padding: 12px 13px;
  cursor: pointer;
  border: 1px solid rgba(67, 72, 87, 0.92);
  border-radius: 8px;
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.07), transparent 38%),
    rgba(11, 15, 23, 0.72);
  color: #aeb6c8;
  text-align: left;
  transition: border-color 0.16s ease, background 0.16s ease, box-shadow 0.16s ease;
}

.job-category-tab:hover {
  border-color: rgba(25, 151, 198, 0.55);
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.11), transparent 40%),
    rgba(14, 19, 29, 0.86);
}

.job-category-tab.active {
  border-color: rgba(25, 151, 198, 0.82);
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.18), transparent 42%),
    rgba(12, 18, 28, 0.92);
  box-shadow: inset 0 0 0 1px rgba(25, 151, 198, 0.1);
}

.category-eyebrow {
  color: #1997c6;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.1em;
  line-height: 1;
  text-transform: uppercase;
}

.job-category-tab strong {
  color: #dce1eb;
  font-size: 14px;
  line-height: 1.2;
}

.category-count {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #7f899b;
  font-size: 11px;
  font-weight: 800;
}

.category-count b {
  padding: 2px 7px;
  border: 1px solid rgba(27, 201, 142, 0.32);
  border-radius: 999px;
  background: rgba(27, 201, 142, 0.1);
  color: #89dfbd;
  font-size: 10px;
  white-space: nowrap;
}

.jobs-category-panel {
  margin: 12px 0 32px;
  padding: 14px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 8px;
  background:
    radial-gradient(circle at 8% 0%, rgba(25, 151, 198, 0.06), transparent 38%),
    rgba(8, 11, 17, 0.5);
}

.jobs-category-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.52);
}

.jobs-category-head h5 {
  margin: 2px 0 5px;
  color: #dce1eb;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.jobs-category-head p {
  max-width: 760px;
  margin: 0;
  color: #8f98aa;
  font-size: 13px;
  line-height: 1.45;
}

.jobs-head-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.live-connection {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 999px;
  padding: 7px 10px;
  color: #aab5c7;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.live-connection i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f5c84c;
  box-shadow: 0 0 8px rgba(245, 200, 76, 0.45);
}

.live-connection.online {
  color: #7ef0c9;
  border-color: rgba(20, 184, 166, 0.32);
}

.live-connection.online i {
  background: #18d59f;
  box-shadow: 0 0 10px rgba(24, 213, 159, 0.55);
}

.job-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.job-workflow-card {
  display: grid;
  gap: 12px;
  min-height: 250px;
  padding: 14px;
  cursor: pointer;
  border: 1px solid rgba(67, 72, 87, 0.92);
  border-radius: 8px;
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.06), transparent 36%),
    rgba(11, 15, 23, 0.72);
  transition: border-color 0.16s ease, background 0.16s ease, transform 0.16s ease;
}

.job-workflow-card:hover,
.job-workflow-card.is-active {
  border-color: rgba(25, 151, 198, 0.62);
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.11), transparent 38%),
    rgba(13, 18, 28, 0.88);
}

.job-workflow-card.is-featured {
  border-color: rgba(25, 151, 198, 0.64);
  box-shadow: inset 3px 0 0 rgba(25, 151, 198, 0.68);
}

.job-workflow-card.is-hidden {
  border-style: dashed;
}

.job-card-top,
.job-card-actions,
.job-card-facts {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.job-card-top {
  align-items: flex-start;
}

.job-card-title-row h5 {
  margin: 0 0 6px;
  color: #e7e9ee;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.22;
}

.job-card-title-row p {
  min-height: 38px;
  margin: 0;
  color: #9aa3b4;
  font-size: 13px;
  line-height: 1.45;
}

.job-card-facts {
  padding: 9px 0;
  border-top: 1px solid rgba(67, 72, 87, 0.42);
  border-bottom: 1px solid rgba(67, 72, 87, 0.42);
}

.job-card-facts div {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.job-card-facts span {
  color: #6f788b;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.job-card-facts strong {
  overflow: hidden;
  color: #d5dbe7;
  font-size: 12px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-card-result {
  min-height: 34px;
}

.job-card-actions {
  margin-top: auto;
}

.job-card-action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.job-card-actions a {
  color: #8f98aa;
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
}

.job-card-actions a:hover {
  color: #1fb2e8;
}

.jobs-empty {
  padding: 26px;
  color: #7f899b;
  font-size: 13px;
  text-align: center;
}

/* Jobs table (replaces the old card grid; reuses the shared AdminTable). */
.jobs-admin-table {
  margin: 0 0 30px;
}

.jobs-admin-table >>> .gc-admin-table {
  min-width: 1065px;
}

.jobs-admin-table >>> .gc-table-cell {
  padding: 12px;
  vertical-align: middle;
}

.jobs-admin-table >>> .gc-table-cell:not(:first-child) {
  padding-top: 12px;
}

.jobs-admin-table >>> .gc-table-row:hover {
  background: rgba(25, 151, 198, 0.045);
}

.jobs-admin-table >>> .gc-table-row + .gc-table-row .gc-table-cell {
  border-top: 1px solid rgba(67, 72, 87, 0.34);
}

.job-cell-main {
  display: grid;
  gap: 5px;
  max-width: 520px;
}

.job-name-line {
  color: #e7e9ee;
  font-size: 11pt;
  font-weight: 600;
}

.job-desc-sub {
  max-width: 420px;
  overflow: hidden;
  color: #9198aa;
  font-size: 9pt;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-advanced-note {
  color: #707a8d;
  font-size: 10px;
  font-weight: 700;
}

.job-schedule-state {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #8f98aa;
  font-size: 12px;
  font-weight: 700;
}

.job-schedule-state i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #687285;
}

.job-schedule-state.automated {
  color: #9bd9c1;
}

.job-schedule-state.automated i {
  background: #1bc98e;
  box-shadow: 0 0 8px rgba(27, 201, 142, 0.42);
}

.job-result-summary {
  display: block;
  overflow: hidden;
  color: #aab3c3;
  font-size: 12px;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-result-summary.tone-good { color: #9bd9c1; }
.job-result-summary.tone-warn { color: #e0c07e; }
.job-result-summary.tone-bad { color: #f0a1af; }
.job-result-summary.tone-live { color: #73d8fb; }

.ai-badge-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.job-ai-badge-row {
  gap: 5px;
  margin-top: 8px;
}

.panel-ai-row {
  margin-top: 0;
}

.ai-prompt-badge {
  display: inline-grid;
  max-width: 245px;
  min-height: 28px;
  padding: 5px 8px;
  border: 1px solid rgba(25, 151, 198, 0.38);
  border-radius: 7px;
  background: rgba(25, 151, 198, 0.08);
  color: #9adef5;
  cursor: pointer;
  font-size: 10px;
  font-weight: 800;
  gap: 2px;
  line-height: 1.2;
  text-align: left;
}

.ai-prompt-badge span,
.ai-prompt-badge small,
.ai-prompt-badge b {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-prompt-badge small {
  color: #7f899b;
  font-size: 9px;
  font-weight: 800;
}

.ai-prompt-badge b {
  color: #e0c07e;
  font-size: 9px;
  font-weight: 900;
  text-transform: uppercase;
}

.ai-prompt-badge:hover {
  border-color: rgba(31, 178, 232, 0.82);
  background: rgba(31, 178, 232, 0.14);
}

.job-prompt-badge {
  display: inline-flex;
  align-items: center;
  max-width: 190px;
  min-height: 23px;
  padding: 0 8px;
  border-radius: 999px;
  gap: 0;
}

.job-prompt-badge span {
  display: block;
  max-width: 174px;
  font-size: 9.5px;
  line-height: 1;
}

.job-prompt-badge small {
  display: none;
}

.job-prompt-badge b {
  display: inline-block;
  width: 7px;
  height: 7px;
  margin-left: 6px;
  overflow: hidden;
  border-radius: 50%;
  background: #e0c07e;
  color: transparent;
  text-indent: -999px;
}

.ai-prompt-badge.warning {
  border-color: rgba(224, 192, 126, 0.42);
  background: rgba(77, 58, 31, 0.28);
}

.run-ai-line {
  color: #7f899b;
  font-size: 10.5px;
  font-weight: 800;
}

.ai-detail-card {
  border-color: rgba(25, 151, 198, 0.32);
  background: rgba(7, 21, 31, 0.28);
}

.ai-outcome-card {
  border-color: rgba(224, 192, 126, 0.3);
  background: rgba(12, 16, 23, 0.5);
}

.ai-outcome-chip {
  padding: 2px 8px;
  border: 1px solid rgba(117, 124, 146, 0.42);
  border-radius: 999px;
  color: #aeb6c8;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
}

.ai-outcome-chip.tone-good {
  border-color: rgba(27, 201, 142, 0.42);
  background: rgba(27, 201, 142, 0.08);
  color: #8fe8c4;
}

.ai-outcome-chip.tone-warn {
  border-color: rgba(224, 192, 126, 0.46);
  background: rgba(77, 58, 31, 0.16);
  color: #e0c07e;
}

.ai-outcome-chip.tone-bad {
  border-color: rgba(232, 79, 106, 0.5);
  background: rgba(90, 34, 48, 0.18);
  color: #f0a1af;
}

.ai-outcome-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  color: #8f98aa;
  font-size: 11px;
  font-weight: 800;
}

.ai-outcome-meta.drawer {
  align-items: center;
}

.ai-output-preview {
  border-top: 1px solid rgba(67, 72, 87, 0.44);
  padding-top: 7px;
}

.ai-output-preview summary {
  color: #9bdff5;
  font-size: 11px;
  font-weight: 900;
  cursor: pointer;
}

.ai-output-preview pre {
  max-height: 180px;
  margin: 8px 0 0;
  padding: 9px;
  border: 1px solid rgba(67, 72, 87, 0.52);
  border-radius: 7px;
  background: rgba(0, 0, 0, 0.22);
  color: #bfc8d8;
  font-size: 11px;
  white-space: pre-wrap;
  overflow: auto;
}

.ai-usage-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ai-usage-chip {
  display: grid;
  gap: 2px;
  padding: 5px 8px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 6px;
  background: rgba(7, 11, 18, 0.48);
  color: #cdd5e1;
  font-size: 10px;
}

.ai-usage-chip b {
  color: #9adef5;
}

.ai-usage-chip small {
  color: #8f98aa;
}

.sched-sub {
  display: block;
  margin-top: 2px;
  color: #6b7180;
  font-size: 8.5pt;
}

.job-row-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
}

.jobs-admin-table .job-row-actions .btn-run {
  min-width: 80px;
  height: 31px;
}

.job-row-actions a {
  color: #9198aa;
  cursor: pointer;
  font-size: 9pt;
}

.job-row-actions a:hover {
  color: #1997c6;
}

.btn-row-settings {
  height: 30px;
  padding: 0 9px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 5px;
  background: transparent;
  color: #9aa3b4;
  cursor: pointer;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.btn-row-settings:hover {
  border-color: rgba(25, 151, 198, 0.64);
  color: #8fddf6;
}

/* Pin a thin accent on the main workflow / live rows. */
.jobs-admin-table >>> .job-row-featured td:first-child {
  box-shadow: inset 3px 0 0 rgba(25, 151, 198, 0.7);
}

.jobs-admin-table >>> .job-row-live td:first-child {
  box-shadow: inset 3px 0 0 rgba(27, 201, 142, 0.8);
}

.jobs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.job-card {
  display: flex;
  min-height: 196px;
  flex-direction: column;
  padding: 16px;
  cursor: pointer;
  border: 1px solid rgba(67, 72, 87, 0.92);
  border-radius: 8px;
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.08), transparent 34%),
    rgba(14, 18, 27, 0.78);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.035),
    0 16px 34px rgba(0, 0, 0, 0.2);
  transition: border-color 0.18s ease, background 0.18s ease;
}

.job-card:hover,
.job-card.is-active {
  border-color: rgba(25, 151, 198, 0.62);
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.12), transparent 36%),
    rgba(16, 21, 31, 0.86);
}

.job-card.is-featured {
  border-color: rgba(25, 151, 198, 0.55);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 0 0 1px rgba(25, 151, 198, 0.18),
    0 16px 34px rgba(0, 0, 0, 0.24);
}

.adv-toggle {
  margin-bottom: 7px;
  color: #9aa3b4;
  font-size: 12px;
  font-weight: 700;
}

.featured-tag,
.hidden-tag,
.test-run-tag {
  display: inline-flex;
  align-items: center;
  height: 21px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 1;
  text-transform: uppercase;
}

.featured-tag {
  border: 1px solid rgba(25, 151, 198, 0.45);
  background: rgba(25, 151, 198, 0.14);
  color: #7bccee;
}

.hidden-tag {
  border: 1px solid rgba(117, 124, 146, 0.34);
  background: rgba(58, 63, 77, 0.5);
  color: #9aa3b4;
}

.test-run-tag {
  border: 1px solid rgba(27, 201, 142, 0.44);
  background: rgba(27, 201, 142, 0.11);
  color: #89dfbd;
}

.job-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.job-title-block {
  min-width: 0;
}

.job-meta-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.job-name {
  margin: 0;
  color: #dce1eb;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
}

.job-desc {
  min-height: 58px;
  margin: 9px 0 12px;
  color: #a4acbc;
  font-size: 13px;
  line-height: 1.45;
}

.cat-badge,
.status-pill {
  display: inline-flex;
  align-items: center;
  height: 21px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 1;
  text-transform: uppercase;
}

.cat-badge {
  padding: 0 8px;
  border: 1px solid rgba(117, 124, 146, 0.32);
  background: rgba(58, 63, 77, 0.6);
  color: #cfd2da;
}

.cat-pipeline {
  border-color: rgba(25, 151, 198, 0.4);
  background: rgba(25, 151, 198, 0.16);
  color: #9adef5;
}

.cat-pricing {
  border-color: rgba(61, 205, 141, 0.28);
  background: rgba(31, 77, 58, 0.56);
  color: #7edfaf;
}

.cat-maintenance {
  border-color: rgba(224, 192, 126, 0.28);
  background: rgba(77, 58, 31, 0.56);
  color: #d4bb81;
}

.cat-research {
  border-color: rgba(103, 123, 230, 0.3);
  background: rgba(42, 47, 90, 0.58);
  color: #aab5ff;
}

.cat-quality {
  border-color: rgba(126, 223, 175, 0.28);
  background: rgba(28, 67, 56, 0.5);
  color: #92e3bd;
}

.cat-reporting {
  border-color: rgba(150, 176, 255, 0.28);
  background: rgba(43, 54, 94, 0.5);
  color: #b7c6ff;
}

.cat-other {
  border-color: rgba(117, 124, 146, 0.32);
  background: rgba(58, 63, 77, 0.6);
  color: #cfd2da;
}

.cat-parts-management {
  border-color: rgba(61, 205, 141, 0.28);
  background: rgba(31, 77, 58, 0.56);
  color: #7edfaf;
}

.cat-parts-quality {
  border-color: rgba(126, 223, 175, 0.28);
  background: rgba(28, 67, 56, 0.5);
  color: #92e3bd;
}

.cat-parts-onboarding {
  border-color: rgba(25, 151, 198, 0.42);
  background: rgba(25, 151, 198, 0.16);
  color: #9adef5;
}

.cat-analytics {
  border-color: rgba(103, 123, 230, 0.3);
  background: rgba(42, 47, 90, 0.58);
  color: #aab5ff;
}

.cat-reports {
  border-color: rgba(150, 176, 255, 0.28);
  background: rgba(43, 54, 94, 0.5);
  color: #b7c6ff;
}

.status-pill {
  padding: 0 9px;
  border: 1px solid rgba(117, 124, 146, 0.28);
  background: rgba(58, 63, 77, 0.52);
  color: #aeb5c5;
}

.st-running,
.st-queued,
.st-starting {
  border-color: rgba(25, 151, 198, 0.36);
  background: rgba(25, 151, 198, 0.14);
  color: #7bccee;
}

.st-success {
  border-color: rgba(27, 201, 142, 0.34);
  background: rgba(27, 201, 142, 0.12);
  color: #89dfbd;
}

.st-failed {
  border-color: rgba(232, 79, 106, 0.38);
  background: rgba(232, 79, 106, 0.12);
  color: #f0a1af;
}

.st-cancelled {
  border-color: rgba(224, 192, 126, 0.28);
  background: rgba(77, 58, 31, 0.52);
  color: #d4bb81;
}

.st-none,
.st-never {
  color: #8e96a8;
}

.job-section {
  padding-top: 12px;
  margin-top: 10px;
  border-top: 1px solid rgba(67, 72, 87, 0.68);
}

.section-title {
  margin-bottom: 9px;
  color: #8f98aa;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.section-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 11px;
}

.section-title-row .section-title { margin-bottom: 4px; }

.section-subnote {
  margin: 0;
  max-width: 360px;
  color: #767f92;
  font-size: 11.5px;
  line-height: 1.45;
}

.section-subnote strong { color: #aeb5c5; font-weight: 700; }

.config-save {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
}

.config-state {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.config-state.is-saved { color: #6f788b; }
.config-state.is-flash { color: #89dfbd; }
.config-state.is-dirty { color: #e0c07e; }

.btn-save-config {
  min-width: 96px;
  height: 31px;
  padding: 0 12px;
  border: 1px solid rgba(25, 151, 198, 0.8);
  border-radius: 6px;
  background: rgba(25, 151, 198, 0.12);
  color: #1fb2e8;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease, color 0.18s ease;
}

.btn-save-config:hover:not(:disabled) {
  border-color: #1fb2e8;
  background: rgba(25, 151, 198, 0.22);
  color: #eaf7fc;
}

.btn-save-config:disabled {
  border-color: rgba(67, 72, 87, 0.8);
  background: rgba(58, 63, 77, 0.32);
  color: #767f92;
  cursor: default;
}

/* Scheduled summary — "this job runs automatically with these params" */
.schedule-summary {
  margin-top: 12px;
  padding: 12px 13px;
  border: 1px solid rgba(25, 151, 198, 0.34);
  border-radius: 8px;
  background:
    radial-gradient(circle at 14% 0%, rgba(25, 151, 198, 0.12), transparent 42%),
    rgba(12, 20, 28, 0.6);
}

.schedule-summary.has-warning {
  border-color: rgba(224, 192, 126, 0.5);
  background:
    radial-gradient(circle at 14% 0%, rgba(224, 192, 126, 0.12), transparent 42%),
    rgba(26, 22, 14, 0.55);
}

.ss-head {
  display: flex;
  align-items: center;
  gap: 9px;
}

.ss-head strong { color: #dce1eb; font-size: 13.5px; font-weight: 700; }

.ss-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border: 1px solid rgba(27, 201, 142, 0.5);
  border-radius: 999px;
  background: rgba(27, 201, 142, 0.12);
  color: #89dfbd;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.ss-next {
  display: flex;
  align-items: baseline;
  gap: 7px;
  margin-top: 9px;
}

.ss-next-label {
  color: #767f92;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.ss-next-value { color: #b8c0d0; font-size: 12.5px; }

.ss-config { margin-top: 10px; }

.ss-config-label {
  display: block;
  margin-bottom: 6px;
  color: #767f92;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.ss-chips { display: flex; flex-wrap: wrap; gap: 6px; }

.ss-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border: 1px solid rgba(67, 72, 87, 0.8);
  border-radius: 6px;
  background: rgba(10, 13, 20, 0.5);
  font-size: 11.5px;
}

.ss-chip b { color: #8f98aa; font-weight: 700; }
.ss-chip span { color: #cfd2da; font-weight: 700; }

.ss-chip.is-changed {
  border-color: rgba(25, 151, 198, 0.55);
  background: rgba(25, 151, 198, 0.1);
}

.ss-chip.is-changed b { color: #7bccee; }
.ss-chip.is-changed span { color: #eaf7fc; }

.ss-chip-empty { color: #6f788b; font-size: 12px; font-style: italic; }

.ss-warn {
  margin-top: 11px;
  padding-top: 10px;
  border-top: 1px solid rgba(224, 192, 126, 0.28);
  color: #e0c07e;
  font-size: 11.5px;
  line-height: 1.45;
}

.ss-warn strong { color: #f0d38c; font-weight: 800; }

/* Card-level scheduled indicator */
.sched-auto { display: inline-flex; align-items: center; gap: 6px; color: #89dfbd !important; }

.sched-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1bc98e;
  box-shadow: 0 0 0 3px rgba(27, 201, 142, 0.18);
}

.job-card-auto {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 9px;
  padding-top: 9px;
  border-top: 1px dashed rgba(67, 72, 87, 0.7);
}

.auto-tag {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border: 1px solid rgba(27, 201, 142, 0.45);
  border-radius: 999px;
  background: rgba(27, 201, 142, 0.1);
  color: #89dfbd;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.auto-config { display: inline-flex; flex-wrap: wrap; gap: 5px; }

.auto-chip {
  padding: 1px 7px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 5px;
  background: rgba(10, 13, 20, 0.45);
  color: #9aa3b4;
  font-size: 10.5px;
  font-weight: 700;
}

.auto-chip.is-changed {
  border-color: rgba(25, 151, 198, 0.5);
  background: rgba(25, 151, 198, 0.1);
  color: #7bccee;
}

.auto-chip-empty { color: #6f788b; font-size: 10.5px; font-style: italic; }

.auto-next { margin-left: auto; color: #767f92; font-size: 10.5px; }

.foot-note {
  margin: 9px 0 0;
  color: #767f92;
  font-size: 11.5px;
  line-height: 1.45;
  text-align: center;
}

.foot-note-warn { display: block; margin-top: 3px; color: #e0c07e; }

.job-options {
  display: grid;
  gap: 7px;
}

.opt-row {
  display: grid;
  grid-template-columns: minmax(135px, 1fr) minmax(118px, 148px);
  align-items: center;
  gap: 12px;
}

.opt-row label {
  margin: 0;
  color: #9aa3b4;
  font-size: 12px;
  line-height: 1.25;
}

.opt-row select,
.opt-row input[type="number"],
.opt-row input[type="text"],
.schedule-number,
.schedule-unit {
  height: 31px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 6px;
  background: rgba(10, 13, 20, 0.56);
  color: #d7dce8;
  padding: 0 9px;
  outline: none;
}

.opt-row select:focus,
.opt-row input:focus,
.schedule-number:focus,
.schedule-unit:focus {
  border-color: rgba(25, 151, 198, 0.85);
  box-shadow: 0 0 0 2px rgba(25, 151, 198, 0.12);
}

.opt-switch {
  display: inline-flex;
  align-items: center;
  justify-self: end;
  gap: 8px;
  cursor: pointer;
}

.opt-switch-text {
  min-width: 22px;
  color: #8f98aa;
  font-size: 12px;
  font-weight: 700;
  text-align: right;
  transition: color 0.18s ease;
}

.opt-switch-text.on {
  color: #cfd2da;
}

.opt-switch input,
.schedule-switch input {
  margin: 0;
}

.opt-row-wide {
  grid-template-columns: 1fr;
  gap: 6px;
}

.opt-textarea {
  width: 100%;
  min-height: 52px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 6px;
  background: rgba(10, 13, 20, 0.56);
  color: #d7dce8;
  padding: 7px 9px;
  font-family: inherit;
  font-size: 12px;
  line-height: 1.45;
  resize: vertical;
  outline: none;
}

.opt-textarea:focus {
  border-color: rgba(25, 151, 198, 0.85);
  box-shadow: 0 0 0 2px rgba(25, 151, 198, 0.12);
}

.opt-textarea::placeholder {
  color: #6f788b;
}

.scope-section {
  border-top-color: rgba(25, 151, 198, 0.34);
}

.scope-state,
.scope-warning {
  padding: 10px 11px;
  border: 1px solid rgba(67, 72, 87, 0.68);
  border-radius: 7px;
  background: rgba(8, 12, 19, 0.42);
  color: #9aa3b4;
  font-size: 12px;
  line-height: 1.45;
}

.scope-error,
.scope-warning {
  border-color: rgba(224, 192, 126, 0.42);
  background: rgba(77, 58, 31, 0.24);
  color: #e0c07e;
}

.scope-preview {
  display: grid;
  gap: 11px;
}

.scope-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 11px;
  border: 1px solid rgba(67, 72, 87, 0.62);
  border-radius: 7px;
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.09), transparent 40%),
    rgba(8, 12, 19, 0.48);
}

.scope-head strong {
  display: block;
  color: #dce4ee;
  font-size: 13px;
  font-weight: 900;
}

.scope-head p {
  margin: 4px 0 0;
  color: #8f98aa;
  font-size: 12px;
  line-height: 1.45;
}

.scope-pill {
  flex: 0 0 auto;
  padding: 3px 8px;
  border: 1px solid rgba(27, 201, 142, 0.36);
  border-radius: 999px;
  background: rgba(27, 201, 142, 0.1);
  color: #89dfbd;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.scope-pills {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.scope-pill.quiet {
  border-color: rgba(117, 124, 146, 0.32);
  background: rgba(58, 63, 77, 0.26);
  color: #aeb5c5;
}

.scope-pill.blocked {
  border-color: rgba(224, 192, 126, 0.42);
  background: rgba(224, 192, 126, 0.1);
  color: #e0c07e;
}

.scope-count-groups,
.scope-detail-groups {
  display: grid;
  gap: 8px;
}

.scope-count-group,
.scope-detail-group {
  display: grid;
  gap: 6px;
}

.scope-count-group > span,
.scope-detail-group > span,
.scope-items-title {
  color: #8f98aa;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.scope-count-group div,
.scope-detail-group div {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.scope-count-group b,
.scope-detail-group b {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border: 1px solid rgba(67, 72, 87, 0.62);
  border-radius: 999px;
  background: rgba(58, 63, 77, 0.2);
  color: #9aa3b4;
  font-size: 10.5px;
  font-weight: 800;
}

.scope-count-group em,
.scope-detail-group em {
  color: #dce4ee;
  font-style: normal;
}

.scope-workflow {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(148px, 1fr));
  gap: 8px;
}

.scope-stage {
  display: grid;
  gap: 4px;
  padding: 9px;
  border: 1px solid rgba(25, 151, 198, 0.28);
  border-radius: 7px;
  background: rgba(25, 151, 198, 0.06);
}

.scope-stage span {
  color: #1fb2e8;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.scope-stage strong {
  color: #dce4ee;
  font-size: 12px;
  font-weight: 900;
}

.scope-stage p {
  margin: 0;
  color: #8f98aa;
  font-size: 11px;
  line-height: 1.35;
}

.scope-directives {
  display: grid;
  gap: 7px;
}

.scope-directives-manual {
  margin-top: 3px;
  padding-top: 10px;
  border-top: 1px dashed rgba(224, 192, 126, 0.28);
}

.scope-directive {
  display: grid;
  grid-template-columns: 20px auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 9px 11px;
  border: 1px solid rgba(67, 72, 87, 0.7);
  border-left-width: 3px;
  border-radius: 8px;
  background: rgba(20, 25, 35, 0.4);
  opacity: 0.6;
}

.scope-directive.is-selected { opacity: 1; background: rgba(20, 25, 35, 0.62); }
.scope-directive.is-report-only { opacity: 1; background: rgba(77, 58, 31, 0.14); }
.scope-directive.tier-platform-keystone { border-left-color: #e8536a; }
.scope-directive.tier-subpath-keystone { border-left-color: #e8924f; }
.scope-directive.tier-variety { border-left-color: #e0c07e; }
.scope-directive.retire { border-left-color: #e8924f; border-style: dashed; }

.sd-rank { color: #6f788b; font-size: 11px; font-weight: 800; text-align: center; }

.tier-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 5px;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.tier-badge.tier-platform-keystone { border: 1px solid rgba(232, 83, 106, 0.55); background: rgba(232, 83, 106, 0.16); color: #f4889a; }
.tier-badge.tier-subpath-keystone { border: 1px solid rgba(232, 146, 79, 0.5); background: rgba(232, 146, 79, 0.14); color: #efb27e; }
.tier-badge.tier-variety { border: 1px solid rgba(224, 192, 126, 0.45); background: rgba(224, 192, 126, 0.12); color: #e0c07e; }

.sd-main { min-width: 0; }
.sd-title-row { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.sd-title { color: #e7ebf3; font-size: 12.5px; font-weight: 700; }
.sd-blocks { color: #f0a1af; font-size: 10.5px; font-weight: 700; }
.sd-cons { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 5px; }

.sd-con {
  padding: 1px 7px;
  border: 1px solid rgba(67, 72, 87, 0.7);
  border-radius: 5px;
  background: rgba(58, 63, 77, 0.3);
  color: #aeb5c5;
  font-size: 10px;
}

.sd-note { margin-top: 5px; color: #8f98aa; font-size: 11px; line-height: 1.4; }

.sd-flag {
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  white-space: nowrap;
}

.sd-flag.in-run { border: 1px solid rgba(27, 201, 142, 0.42); background: rgba(27, 201, 142, 0.1); color: #89dfbd; }
.sd-flag.queued,
.sd-flag.not-selected { border: 1px solid rgba(117, 124, 146, 0.34); background: rgba(58, 63, 77, 0.4); color: #9aa3b4; }
.sd-flag.report-only { border: 1px solid rgba(224, 192, 126, 0.4); background: rgba(224, 192, 126, 0.1); color: #e0c07e; }

.scope-items {
  display: grid;
  gap: 7px;
  overflow: auto;
}

.scope-items table {
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;
  border: 1px solid rgba(67, 72, 87, 0.58);
  border-radius: 7px;
  background: rgba(7, 11, 18, 0.34);
  font-size: 11px;
}

.scope-items th,
.scope-items td {
  padding: 7px 8px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.38);
  color: #b8c0d0;
  text-align: left;
  vertical-align: top;
}

.scope-items th {
  color: #7f899b;
  font-size: 9.5px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.scope-items tr:last-child td {
  border-bottom: 0;
}

.result-note {
  display: grid;
  gap: 3px;
  padding: 7px 9px;
  border: 1px solid rgba(103, 123, 230, 0.28);
  border-radius: 6px;
  background: rgba(42, 47, 90, 0.28);
}

.note-label {
  color: #aab5ff;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.note-text {
  color: #cfd4e6;
  font-size: 12px;
  line-height: 1.45;
  white-space: pre-wrap;
}

.log-note {
  display: grid;
  gap: 3px;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.84);
  background: rgba(42, 47, 90, 0.2);
}

.schedule-section {
  margin-top: auto;
}

.schedule-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.schedule-switch {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin: 0;
  color: #cfd2da;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.schedule-switch input,
.opt-switch input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.switch-track {
  position: relative;
  width: 35px;
  height: 19px;
  border: 1px solid rgba(117, 124, 146, 0.7);
  border-radius: 999px;
  background: rgba(10, 13, 20, 0.72);
  transition: border-color 0.18s ease, background 0.18s ease;
}

.switch-track::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #8f98aa;
  transition: transform 0.18s ease, background 0.18s ease;
}

.schedule-switch input:checked + .switch-track,
.opt-switch input:checked + .switch-track {
  border-color: rgba(25, 151, 198, 0.88);
  background: rgba(25, 151, 198, 0.16);
}

.schedule-switch input:checked + .switch-track::after,
.opt-switch input:checked + .switch-track::after {
  transform: translateX(16px);
  background: #1fb2e8;
}

.schedule-builder {
  display: grid;
  grid-template-columns: auto 74px minmax(96px, 1fr);
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  color: #b8c0d0;
  font-size: 13px;
}

.schedule-builder.is-disabled {
  opacity: 0.48;
}

.schedule-number {
  width: 74px;
}

.schedule-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 9px;
  color: #7f899b;
  font-size: 12px;
  line-height: 1.35;
}

.cron-code {
  flex: 0 0 auto;
  max-width: 150px;
  overflow: hidden;
  padding: 3px 7px;
  border: 1px solid rgba(67, 72, 87, 0.7);
  border-radius: 5px;
  color: #8e96a8;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-summary {
  margin-top: 4px;
}

.job-stats-empty {
  margin-top: 8px;
  color: #6f788b;
  font-size: 12px;
  font-style: italic;
}

.job-foot {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-top: auto;
  padding-top: 14px;
}

.sched-hint {
  margin-top: 2px;
  color: #6f788b;
  font-size: 11px;
}

.card-open-hint {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 10px;
  padding-top: 9px;
  border-top: 1px solid rgba(67, 72, 87, 0.55);
  color: #7f899b;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  transition: color 0.16s ease;
}

.job-card:hover .card-open-hint {
  color: #1fb2e8;
}

/* Job detail drawer */
.job-drawer {
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.55);
}

.job-panel {
  display: flex;
  width: 460px;
  max-width: 94vw;
  height: 100%;
  flex-direction: column;
  border-left: 1px solid rgba(67, 72, 87, 0.95);
  background: #11151f;
  box-shadow: -22px 0 70px rgba(0, 0, 0, 0.5);
  animation: panel-in 0.2s ease;
}

@keyframes panel-in {
  from { transform: translateX(28px); opacity: 0.4; }
  to { transform: translateX(0); opacity: 1; }
}

.panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.84);
}

.panel-title {
  margin: 9px 0 0;
  color: #dce1eb;
  font-size: 19px;
  font-weight: 700;
}

.panel-head .close {
  color: #9aa3b4;
  cursor: pointer;
  font-size: 22px;
  line-height: 1;
  text-decoration: none;
}

.panel-head .close:hover {
  color: #e6ebf2;
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 4px 18px 18px;
}

.panel-desc {
  margin: 14px 0 4px;
  color: #a4acbc;
  font-size: 13px;
  line-height: 1.5;
}

.panel-lastrun {
  margin-bottom: 4px;
  color: #9aa3b4;
  font-size: 12px;
}

.panel-foot {
  padding: 14px 18px;
  border-top: 1px solid rgba(67, 72, 87, 0.84);
}

.panel-foot-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 9px;
}

.panel-foot-actions.has-test-delivery {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.btn-run.wide {
  width: 100%;
  height: 40px;
}

.btn-test-delivery {
  min-width: 132px;
  height: 35px;
  padding: 0 12px;
  border: 1px solid rgba(27, 201, 142, 0.65);
  border-radius: 6px;
  background: rgba(27, 201, 142, 0.1);
  color: #89dfbd;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  transition: border-color 0.18s ease, background 0.18s ease, color 0.18s ease;
}

.btn-test-delivery:hover:not(:disabled) {
  border-color: rgba(63, 226, 180, 0.9);
  background: rgba(27, 201, 142, 0.18);
  color: #d7fff1;
}

.btn-test-delivery.wide {
  width: 100%;
  height: 40px;
}

.btn-test-delivery:disabled {
  border-color: rgba(67, 72, 87, 0.8);
  background: rgba(58, 63, 77, 0.42);
  color: #8f98aa;
  cursor: default;
}

.test-delivery-note {
  display: block;
  margin-top: 5px;
  color: #89cdb6;
}

.last-run {
  display: grid;
  gap: 2px;
  min-width: 0;
  color: #9aa3b4;
  font-size: 12px;
}

.foot-label {
  color: #6f788b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.job-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 7px;
}

.btn-run,
.btn-refresh {
  min-width: 92px;
  height: 35px;
  border: 1px solid rgba(25, 151, 198, 0.8);
  border-radius: 6px;
  background: rgba(25, 151, 198, 0.1);
  color: #1fb2e8;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  transition: border-color 0.18s ease, background 0.18s ease, color 0.18s ease;
}

.btn-run:hover,
.btn-refresh:hover {
  border-color: #1fb2e8;
  background: rgba(25, 151, 198, 0.2);
  color: #eaf7fc;
}

.btn-run.warning {
  border-color: rgba(224, 192, 126, 0.72);
  background: rgba(77, 58, 31, 0.16);
  color: #e0c07e;
}

.btn-run.warning:hover {
  border-color: rgba(240, 211, 140, 0.9);
  background: rgba(77, 58, 31, 0.28);
  color: #ffe5a4;
}

.btn-run.danger {
  border-color: rgba(232, 79, 106, 0.78);
  background: rgba(90, 34, 48, 0.2);
  color: #f0a1af;
}

.btn-run.danger:hover {
  border-color: rgba(255, 129, 151, 0.94);
  background: rgba(90, 34, 48, 0.34);
  color: #ffdbe2;
}

.btn-run:disabled {
  border-color: rgba(67, 72, 87, 0.8);
  background: rgba(58, 63, 77, 0.42);
  color: #8f98aa;
  cursor: default;
}

.btn-run.is-live:disabled {
  border-color: rgba(25, 151, 198, 0.78);
  background: rgba(25, 151, 198, 0.18);
  color: #9adef5;
  box-shadow: inset 0 0 0 1px rgba(25, 151, 198, 0.14);
}

.btn-run.is-starting:disabled {
  animation: run-starting-pulse 1.2s ease-in-out infinite;
}

@keyframes run-starting-pulse {
  0%, 100% { box-shadow: inset 0 0 0 1px rgba(25, 151, 198, 0.1); }
  50% { box-shadow: inset 0 0 0 1px rgba(31, 178, 232, 0.42), 0 0 0 2px rgba(31, 178, 232, 0.08); }
}

.btn-refresh {
  min-width: 84px;
  height: 31px;
}

.runs-label {
  margin-top: 2px;
}

.runs-category-note {
  display: block;
  margin-top: 4px;
  color: #7f889a;
  font-size: 9pt;
}

.runs-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.runs-filter {
  height: 31px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 6px;
  background: rgba(10, 13, 20, 0.56);
  color: #cfd2da;
  padding: 0 9px;
  font-size: 12px;
  outline: none;
}

.runs-filter:focus {
  border-color: rgba(25, 151, 198, 0.85);
  box-shadow: 0 0 0 2px rgba(25, 151, 198, 0.12);
}

.runs-history-list {
  display: grid;
  gap: 10px;
  overflow: hidden;
  margin-top: 12px;
  padding: 10px;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 8px;
  background: rgba(7, 11, 18, 0.34);
}

.runs-history-head,
.run-history-main {
  display: grid;
  grid-template-columns: minmax(190px, 1.35fr) 116px 100px 130px 96px 94px;
  align-items: center;
  gap: 14px;
}

.runs-history-head {
  min-height: 34px;
  margin: -10px -10px 0;
  padding: 0 22px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.76);
  background: rgba(7, 11, 18, 0.42);
  color: #7f899b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.run-history-card {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(67, 72, 87, 0.64);
  border-radius: 8px;
  background:
    linear-gradient(90deg, rgba(12, 17, 26, 0.72), rgba(7, 11, 18, 0.38)),
    rgba(7, 11, 18, 0.26);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.012);
  transition: background 160ms ease, border-color 160ms ease;
}

.run-history-card:last-of-type {
  border-bottom-color: rgba(67, 72, 87, 0.64);
}

.run-history-card::before {
  position: absolute;
  top: 10px;
  bottom: 10px;
  left: 8px;
  width: 3px;
  border-radius: 999px;
  background: rgba(127, 137, 155, 0.28);
  content: "";
}

.run-history-card.row-success::before {
  background: rgba(27, 201, 142, 0.5);
}

.run-history-card.row-failed::before {
  background: rgba(232, 79, 106, 0.86);
}

.run-history-card.row-running::before,
.run-history-card.row-queued::before {
  background: rgba(31, 178, 232, 0.86);
}

.run-history-card.row-cancelled::before {
  background: rgba(224, 192, 126, 0.72);
}

.run-history-card:hover,
.run-history-card.is-expanded {
  border-color: rgba(25, 151, 198, 0.46);
  background:
    linear-gradient(90deg, rgba(14, 22, 32, 0.82), rgba(7, 11, 18, 0.48)),
    rgba(25, 151, 198, 0.035);
}

.run-history-main {
  min-height: 58px;
  padding: 14px 14px 12px 22px;
  border-bottom: 1px solid rgba(47, 51, 61, 0.48);
  color: #cfd2da;
}

.run-job-title {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.run-id {
  color: #687285;
  font-size: 11px;
  font-weight: 700;
}

.run-history-result {
  padding: 12px 14px 14px 22px;
}

.run-history-result .result-cell {
  max-width: none;
  padding: 13px 14px;
  border: 1px solid rgba(67, 72, 87, 0.52);
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(10, 15, 23, 0.58), rgba(6, 10, 16, 0.42)),
    rgba(7, 11, 18, 0.3);
}

.run-history-result .run-pipeline-head {
  align-items: center;
  margin-bottom: 11px;
}

.run-history-result .run-stage-line {
  max-width: none;
}

.run-history-card .run-stage-track {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(145px, 1fr));
  gap: 9px;
  max-width: none;
}

.run-history-card .run-stage-step {
  grid-template-columns: 13px minmax(0, 1fr);
  min-height: 54px;
  padding: 9px 10px;
  border-color: rgba(67, 72, 87, 0.68);
  background: rgba(8, 12, 19, 0.5);
}

.run-history-card .run-stage-step::before {
  display: none;
}

.run-history-card .stage-prompt {
  grid-column: 2;
  justify-self: start;
  max-width: 100%;
  margin-top: 3px;
}

.run-history-card .stage-summary {
  white-space: normal;
}

.runs-history-empty {
  padding: 24px 12px;
  color: #8f98aa;
  font-size: 13px;
  text-align: center;
}

.status-pill.is-live {
  animation: pill-pulse 1.6s ease-in-out infinite;
}

@keyframes pill-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(25, 151, 198, 0.0); }
  50% { box-shadow: 0 0 0 3px rgba(25, 151, 198, 0.18); }
}

.runs-table-wrap {
  margin-top: 12px;
  overflow-x: auto;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 8px;
  background: rgba(9, 13, 20, 0.42);
}

.runs-table {
  width: 100%;
  min-width: 900px;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;
}

.compact-runs-table .runs-table th:nth-child(1) { width: 22%; }
.compact-runs-table .runs-table th:nth-child(2) { width: 10%; }
.compact-runs-table .runs-table th:nth-child(3) { width: 9%; }
.compact-runs-table .runs-table th:nth-child(4) { width: 11%; }
.compact-runs-table .runs-table th:nth-child(5) { width: 9%; }
.compact-runs-table .runs-table th:nth-child(6) { width: 24%; }
.compact-runs-table .runs-table th:nth-child(7) { width: 15%; }

.runs-table th {
  padding: 10px 11px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.8);
  color: #7f899b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-align: left;
  text-transform: uppercase;
}

.runs-table td {
  padding: 10px 11px;
  border-bottom: 1px solid rgba(47, 51, 61, 0.84);
  color: #cfd2da;
}

.compact-runs-table .runs-table td {
  height: 54px;
  vertical-align: middle;
}

.compact-runs-table .job-cell {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.compact-runs-table .job-cell strong {
  overflow: hidden;
  color: #dce1eb;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.compact-result-summary {
  min-width: 0;
  flex-wrap: nowrap;
}

.run-result-headline {
  display: block;
  overflow: hidden;
  color: #aab3c3;
  font-size: 12px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.run-result-headline.tone-good { color: #9bd9c1; }
.run-result-headline.tone-warn { color: #e0c07e; }
.run-result-headline.tone-bad { color: #f0a1af; }
.run-result-headline.tone-live { color: #73d8fb; }

.runs-table tbody tr:hover {
  background: rgba(25, 151, 198, 0.05);
}

.runs-table tbody td:first-child {
  position: relative;
}

.runs-table tbody tr.row-success td:first-child::before,
.runs-table tbody tr.row-failed td:first-child::before,
.runs-table tbody tr.row-running td:first-child::before,
.runs-table tbody tr.row-queued td:first-child::before,
.runs-table tbody tr.row-cancelled td:first-child::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 3px;
}

.runs-table tbody tr.row-success td:first-child::before {
  background: rgba(27, 201, 142, 0.4);
}

.runs-table tbody tr.row-failed td:first-child::before {
  background: #e84f6a;
}

.runs-table tbody tr.row-running td:first-child::before,
.runs-table tbody tr.row-queued td:first-child::before {
  background: #1fb2e8;
}

.runs-table tbody tr.row-cancelled td:first-child::before {
  background: rgba(224, 192, 126, 0.7);
}

.runs-table tbody tr.is-expanded {
  background: rgba(25, 151, 198, 0.045);
}

.runs-table tbody tr.is-expanded:hover {
  background: rgba(25, 151, 198, 0.06);
}

.job-cell {
  font-weight: 600;
  color: #dce1eb;
}

.trigger-cell {
  color: #9aa3b4;
  text-transform: capitalize;
}

.result-cell {
  max-width: 480px;
  color: #9aa3b4;
  vertical-align: top;
  white-space: normal;
}

.stat-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.stat-chip {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  padding: 2px 8px;
  border: 1px solid rgba(67, 72, 87, 0.7);
  border-radius: 6px;
  background: rgba(58, 63, 77, 0.3);
  font-size: 11px;
  line-height: 1.55;
  white-space: nowrap;
}

.stat-chip .chip-key {
  color: #8b93a5;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: capitalize;
}

.stat-chip .chip-val {
  color: #dce1eb;
  font-size: 11.5px;
  font-weight: 800;
}

.stat-chip.tone-good {
  border-color: rgba(27, 201, 142, 0.34);
  background: rgba(27, 201, 142, 0.1);
}

.stat-chip.tone-good .chip-val {
  color: #89dfbd;
}

.stat-chip.tone-warn {
  border-color: rgba(224, 192, 126, 0.34);
  background: rgba(224, 192, 126, 0.1);
}

.stat-chip.tone-warn .chip-val {
  color: #e0c07e;
}

.stat-chip.tone-bad {
  border-color: rgba(232, 79, 106, 0.4);
  background: rgba(232, 79, 106, 0.12);
}

.stat-chip.tone-bad .chip-val {
  color: #f0a1af;
}

.chip-denom {
  margin-left: 1px;
  color: #6f788b;
  font-size: 10px;
  font-weight: 700;
}

.chip-stage {
  color: #6f788b;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.chip-stage::after {
  content: "\00b7";
  margin: 0 2px 0 3px;
  color: #565d6e;
}

.stat-chip.is-zero {
  opacity: 0.42;
}

.stat-chip.is-param {
  border-style: dashed;
  border-color: rgba(67, 72, 87, 0.7);
  background: rgba(58, 63, 77, 0.18);
}

.stat-chip.is-param .chip-val {
  color: #aeb5c5;
  font-weight: 700;
}

.result-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
}

.run-pipeline-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.run-pipeline-copy {
  display: grid;
  min-width: 0;
  gap: 4px;
}

.run-stage-current {
  color: #dce1eb;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.run-stage-current.stage-live {
  color: #58d4ff;
}

.run-stage-current.stage-good {
  color: #89dfbd;
}

.run-stage-current.stage-warn {
  color: #e0c07e;
}

.run-stage-current.stage-bad {
  color: #f0a1af;
}

.run-stage-line {
  max-width: 560px;
  overflow: hidden;
  color: #7f899b;
  font-size: 11px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.run-stage-track {
  display: grid;
  gap: 8px;
  max-width: 560px;
}

.run-stage-step {
  position: relative;
  display: grid;
  grid-template-columns: 13px minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  padding: 7px 8px;
  border: 1px solid rgba(67, 72, 87, 0.62);
  border-radius: 7px;
  background: rgba(7, 11, 18, 0.42);
}

.run-stage-step::before {
  position: absolute;
  top: -9px;
  bottom: 100%;
  left: 14px;
  width: 1px;
  background: rgba(67, 72, 87, 0.55);
  content: "";
}

.run-stage-step:first-child::before {
  display: none;
}

.stage-dot {
  width: 9px;
  height: 9px;
  border: 1px solid rgba(127, 137, 155, 0.78);
  border-radius: 50%;
  background: rgba(127, 137, 155, 0.24);
  box-shadow: 0 0 0 3px rgba(127, 137, 155, 0.08);
}

.run-stage-step.is-completed .stage-dot {
  border-color: rgba(27, 201, 142, 0.8);
  background: #1bc98e;
  box-shadow: 0 0 0 3px rgba(27, 201, 142, 0.11);
}

.run-stage-step.is-running .stage-dot {
  border-color: rgba(31, 178, 232, 0.9);
  background: #1fb2e8;
  box-shadow: 0 0 0 3px rgba(31, 178, 232, 0.16);
  animation: stage-pulse 1.5s ease-in-out infinite;
}

.run-stage-step.is-partial .stage-dot,
.run-stage-step.is-waiting .stage-dot {
  border-color: rgba(224, 192, 126, 0.72);
  background: rgba(224, 192, 126, 0.58);
  box-shadow: 0 0 0 3px rgba(224, 192, 126, 0.1);
}

.run-stage-step.is-failed .stage-dot {
  border-color: rgba(232, 79, 106, 0.9);
  background: #e84f6a;
  box-shadow: 0 0 0 3px rgba(232, 79, 106, 0.14);
}

@keyframes stage-pulse {
  0%, 100% { box-shadow: 0 0 0 3px rgba(31, 178, 232, 0.12); }
  50% { box-shadow: 0 0 0 6px rgba(31, 178, 232, 0.03); }
}

.stage-copy {
  display: grid;
  min-width: 0;
  gap: 2px;
}

.stage-name {
  color: #cfd6e2;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.stage-summary {
  overflow: hidden;
  color: #8f98aa;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stage-prompt {
  max-width: 170px;
  overflow: hidden;
  padding: 3px 7px;
  border: 1px solid rgba(25, 151, 198, 0.46);
  border-radius: 999px;
  background: rgba(25, 151, 198, 0.08);
  color: #8fddf6;
  cursor: pointer;
  font-size: 10px;
  font-weight: 800;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stage-prompt:hover {
  border-color: rgba(31, 178, 232, 0.9);
  background: rgba(31, 178, 232, 0.15);
  color: #eaf7fc;
}

.stage-prompt.detail {
  max-width: none;
  margin-left: auto;
}

.stage-detail-card {
  display: grid;
  gap: 8px;
  padding: 10px;
  border: 1px solid rgba(67, 72, 87, 0.58);
  border-radius: 7px;
  background: rgba(7, 11, 18, 0.36);
}

.stage-detail-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stage-detail-title {
  color: #d7dde8;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.stage-status-pill {
  padding: 2px 7px;
  border: 1px solid rgba(67, 72, 87, 0.75);
  border-radius: 999px;
  color: #8f98aa;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.stage-status-pill.is-completed {
  border-color: rgba(27, 201, 142, 0.42);
  color: #89dfbd;
}

.stage-status-pill.is-running {
  border-color: rgba(31, 178, 232, 0.5);
  color: #58d4ff;
}

.stage-status-pill.is-partial,
.stage-status-pill.is-waiting {
  border-color: rgba(224, 192, 126, 0.46);
  color: #e0c07e;
}

.stage-status-pill.is-failed {
  border-color: rgba(232, 79, 106, 0.5);
  color: #f0a1af;
}

.stage-detail-summary {
  color: #9aa3b4;
  font-size: 12px;
  line-height: 1.35;
}

.result-toggle {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 2px;
  padding: 2px 6px;
  border: 0;
  border-radius: 5px;
  background: transparent;
  color: #1fb2e8;
  cursor: pointer;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.result-toggle:hover {
  background: rgba(25, 151, 198, 0.1);
}

.result-toggle .chev {
  display: inline-block;
  font-size: 13px;
  line-height: 1;
  transition: transform 0.16s ease;
}

.result-toggle .chev.open {
  transform: rotate(90deg);
}

.result-detail {
  display: grid;
  gap: 9px;
  margin-top: 8px;
  padding-top: 9px;
  border-top: 1px solid rgba(67, 72, 87, 0.55);
}

.dedupe-report {
  display: grid;
  gap: 8px;
  padding: 10px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 8px;
  background: rgba(7, 11, 18, 0.34);
}

.dedupe-report-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.dedupe-report-head small {
  color: #8f98aa;
  font-size: 11px;
}

.dedupe-report table,
.dedupe-report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.dedupe-report th,
.dedupe-report td,
.dedupe-report-table th,
.dedupe-report-table td {
  padding: 7px 8px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.52);
  color: #cfd2da;
  text-align: left;
  vertical-align: top;
}

.dedupe-report th,
.dedupe-report-table th {
  color: #7f899b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.stat-group {
  display: grid;
  gap: 5px;
}

.stage-label {
  color: #7f899b;
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.stat-group.params .stage-label {
  color: #6f788b;
}

.result-error {
  display: block;
  max-width: 460px;
  color: #f0a1af;
  font-size: 12px;
  line-height: 1.45;
  white-space: normal;
  word-break: break-word;
}

.result-empty {
  color: #6f788b;
}

.row-actions {
  text-align: right;
  white-space: nowrap;
}

div.row-actions,
.row-actions-inner {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
}

.compact-runs-table td.row-actions {
  display: table-cell;
  vertical-align: middle;
}

.row-actions a {
  margin-left: 0;
  color: #1fb2e8;
  cursor: pointer;
  font-weight: 700;
  text-transform: uppercase;
}

.cancel-link {
  color: #f0a1af !important;
}

.empty {
  color: #6f788b;
  text-align: center;
}

.log-drawer {
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.62);
}

.log-panel {
  display: flex;
  width: 760px;
  max-width: 92vw;
  max-height: 80vh;
  flex-direction: column;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 8px;
  background: #11151f;
  box-shadow: 0 22px 70px rgba(0, 0, 0, 0.44);
}

.log-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.84);
  color: #cfd2da;
}

.log-head .close {
  color: #9aa3b4;
  cursor: pointer;
  font-size: 18px;
  text-decoration: none;
}

.log-body {
  flex: 1;
  margin: 0;
  overflow: auto;
  padding: 14px 16px;
  color: #b6e3c8;
  font-family: monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

.log-ai-section {
  display: grid;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.72);
  background: rgba(7, 11, 18, 0.42);
}

.log-dedupe-section {
  display: grid;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.72);
  background: rgba(7, 11, 18, 0.36);
}

.dedupe-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.dedupe-summary span {
  display: grid;
  gap: 2px;
  min-width: 92px;
  padding: 7px 9px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 7px;
  background: rgba(8, 11, 17, 0.46);
}

.dedupe-summary small {
  color: #7f899b;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.dedupe-summary b {
  color: #dce1eb;
  font-size: 13px;
}

.log-foot {
  padding: 10px 16px;
  border-top: 1px solid rgba(67, 72, 87, 0.84);
  color: #9aa3b4;
  font-size: 13px;
}

@media (max-width: 860px) {
  .jobs-view-tabs {
    display: flex;
  }

  .jobs-view-tabs button {
    flex: 1;
    justify-content: center;
  }

  .jobs-table-toolbar {
    grid-template-columns: 1fr;
  }

  .jobs-head-actions {
    justify-content: space-between;
  }

  .active-category-description {
    grid-column: auto;
  }

  .jobs-grid {
    grid-template-columns: 1fr;
  }

  .runs-history-head {
    display: none;
  }

  .run-history-main {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px 12px;
  }

  .run-history-main > span,
  .run-history-main > div {
    min-width: 0;
  }

  .run-history-main .trigger-cell,
  .run-history-main > span:nth-child(4),
  .run-history-main > span:nth-child(5) {
    color: #8f98aa;
    font-size: 12px;
  }

  .run-history-card .run-stage-track {
    grid-template-columns: 1fr;
  }

  .opt-row,
  .schedule-builder {
    grid-template-columns: 1fr;
  }

  .panel-foot-actions.has-test-delivery {
    grid-template-columns: 1fr;
  }

  .schedule-meta,
  .job-foot {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
